import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, Filter, ArrowLeft, Upload, Play, Code, Image as ImageIcon, Edit, Download, Heart, Star, X, GripVertical, FileText, Trash2, MessageSquare, Shield, ShieldCheck, AlertTriangle, Lock, RefreshCw } from 'lucide-react';
import JSZip from 'jszip';
import WorkflowCard from './components/WorkflowCard';
import FeedbackPanel from './components/FeedbackPanel';
import RejectionModal from './components/RejectionModal';
import AdminFeedbackPanel from './components/AdminFeedbackPanel';
import CommentsSection from './components/CommentsSection';
import CommentsLog from './components/CommentsLog';
import AddPaymentMethodModal from '../account/components/AddPaymentMethodModal';
import SyncPanel from './components/SyncPanel';
import { useWorkOS } from '../workos/context/WorkOSContext';

// API Base URL
const API_BASE = '/.netlify/functions';

const MAIN_CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'video_processing', label: 'Video' },
  { id: 'image_processing', label: 'Image' },
  { id: 'data_tools', label: 'Data' },
  { id: 'file_utilities', label: 'Files' },
  { id: 'games', label: 'Games' },
];

const MORE_CATEGORIES = [
  { id: 'automation', label: 'Automation' },
  { id: 'productivity', label: 'Productivity' },
  { id: 'creative_tools', label: 'Creative Tools' },
  { id: 'developer_tools', label: 'Developer Tools' },
  { id: 'gen_ai', label: 'Generative AI' },
  { id: 'llm', label: 'LLM Tools' },
];

const SORT_OPTIONS = [
  { id: 'updated', label: 'Recently Updated' },
  { id: 'downloads', label: 'Most Downloaded' },
  { id: 'likes', label: 'Most Liked' },
  { id: 'new', label: 'Newest First' },
];

// ============================================
// DETAIL VIEW COMPONENT (Extracted to prevent re-creation on parent re-renders)
// ============================================
const DetailView = ({
  selectedWorkflowId,
  user,
  isAuthenticated,
  getValidAccessToken,
  likedWorkflows,
  setLikedWorkflows,
  setView,
  fetchWorkflows,
  handleLike,
  hasSubscription,
  setEditingWorkflowId,
  navigate,
  isEmbedded = false,
  subscriptionTier,
}) => {
  const [activeTab, setActiveTab] = useState('description');
  const [detailWorkflow, setDetailWorkflow] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailError, setDetailError] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState({ type: 'image', url: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [purchaseConfirmDialog, setPurchaseConfirmDialog] = useState({
    isOpen: false,
    priceCents: 0,
    balanceCents: 0
  });
  const [insufficientCreditsDialog, setInsufficientCreditsDialog] = useState({
    isOpen: false,
    required: 0,
    balance: 0,
    paymentMethod: null // { brand, last4 }
  });
  const [paymentMethodModal, setPaymentMethodModal] = useState({
    isOpen: false,
    context: 'download' // 'download' or 'upload'
  });
  const [isDirectPaying, setIsDirectPaying] = useState(false);

  // Check if current user is admin (simplified check based on email)
  const ADMIN_EMAILS = ['jason@isd.ai', 'julian@isd.ai'];
  const isUserAdmin = user && ADMIN_EMAILS.includes(user.email);

  // Fetch single workflow details
  useEffect(() => {
    const fetchWorkflowDetails = async () => {
      setDetailLoading(true);
      setDetailError(null);

      try {
        const headers = {};

        // Include auth token if user is logged in (for ownership check)
        if (isAuthenticated) {
          try {
            const accessToken = await getValidAccessToken();
            headers['Authorization'] = `Bearer ${accessToken}`;
          } catch (err) {
            console.error('Failed to get access token:', err);
          }
        }

        const response = await fetch(`${API_BASE}/marketplace?id=${selectedWorkflowId}&include=fork_tree`, { headers });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('API Error Response:', response.status, errorText);
          throw new Error(`Failed to load workflow (${response.status})`);
        }

        const data = await response.json();
        console.log('Workflow data loaded:', data.id, data.title);
        setDetailWorkflow(data);

        // Update liked workflows state if user has liked this workflow
        // Use functional update to avoid needing likedWorkflows in dependency array
        // Return same Set object if no change needed to prevent unnecessary re-renders
        if (data.user_has_liked) {
          setLikedWorkflows(prev => {
            if (!prev.has(data.id)) {
              return new Set([...prev, data.id]);
            }
            return prev; // Return same object if already liked - prevents re-render
          });
        }
      } catch (err) {
        console.error('Error fetching workflow details:', err);
        setDetailError(err.message || 'Failed to load workflow');
      } finally {
        setDetailLoading(false);
      }
    };

    if (selectedWorkflowId) {
      fetchWorkflowDetails();
    }
  }, [selectedWorkflowId]); // Only selectedWorkflowId dependency to prevent double fetch

  // Set initial selected media when workflow loads
  useEffect(() => {
    if (detailWorkflow?.thumbnail_url) {
      setSelectedMedia({ type: 'image', url: detailWorkflow.thumbnail_url });
    }
  }, [detailWorkflow?.id]);

  // Delete workflow function
  const handleDeleteWorkflow = async () => {
    if (!confirm('Are you sure you want to delete this workflow? This action will hide it from the marketplace but can be restored by an admin.')) {
      return;
    }

    setIsDeleting(true);
    try {
      const accessToken = await getValidAccessToken();

      const response = await fetch(`${API_BASE}/marketplace-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          listingId: detailWorkflow.id
        })
      });

      if (response.ok) {
        alert('Workflow deleted successfully');
        navigate('/exchange');
        fetchWorkflows();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete workflow');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // Fetch user's credit balance
  const fetchCreditBalance = async () => {
    try {
      const accessToken = await getValidAccessToken();
      const response = await fetch(`${API_BASE}/credits-balance`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (!response.ok) return 0;
      const data = await response.json();
      return data.balance_cents || 0;
    } catch {
      return 0;
    }
  };

  // Check if user has payment method on file (returns { hasPaymentMethod, paymentMethod })
  const checkPaymentMethod = async () => {
    try {
      const accessToken = await getValidAccessToken();
      const response = await fetch(`${API_BASE}/check-payment-method`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (!response.ok) return { hasPaymentMethod: false, paymentMethod: null };
      const data = await response.json();
      return { 
        hasPaymentMethod: data.hasPaymentMethod, 
        paymentMethod: data.paymentMethod // { brand, last4, expMonth, expYear }
      };
    } catch {
      return { hasPaymentMethod: false, paymentMethod: null };
    }
  };

  // Direct charge for workflow purchase (no credits)
  const handleDirectPurchase = async () => {
    setIsDirectPaying(true);
    try {
      const accessToken = await getValidAccessToken();
      const response = await fetch(`${API_BASE}/direct-purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          listingId: detailWorkflow.id,
          amountCents: insufficientCreditsDialog.required
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Payment failed');
      }

      // Payment successful - now download
      setInsufficientCreditsDialog({ isOpen: false, required: 0, balance: 0, paymentMethod: null });
      handleDownload();
    } catch (error) {
      console.error('Direct purchase error:', error);
      alert(`Payment failed: ${error.message}`);
    } finally {
      setIsDirectPaying(false);
    }
  };

  // Handle download button click - show confirmation for paid workflows
  const handleDownloadClick = async () => {
    const workflow = detailWorkflow;

    // If free or already purchased, download directly
    if (!workflow.price_cents || workflow.price_cents === 0 || workflow.user_has_purchased) {
      handleDownload();
      return;
    }

    // For paid workflows, first check credit balance
    const balance = await fetchCreditBalance();

    if (balance >= workflow.price_cents) {
      // Has enough credits - show purchase confirmation (no CC check needed)
      setPurchaseConfirmDialog({
        isOpen: true,
        priceCents: workflow.price_cents,
        balanceCents: balance
      });
    } else {
      // Not enough credits - check if they have CC to add credits
      const { hasPaymentMethod, paymentMethod } = await checkPaymentMethod();
      if (!hasPaymentMethod) {
        // No CC - show add payment method modal first
        setPaymentMethodModal({ isOpen: true, context: 'download' });
      } else {
        // Has CC but not enough credits - show insufficient credits dialog with pay option
        setInsufficientCreditsDialog({
          isOpen: true,
          required: workflow.price_cents,
          balance: balance,
          paymentMethod: paymentMethod // { brand, last4 }
        });
      }
    }
  };

  // Download workflow function
  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      // Try to get access token (may be null for anonymous downloads of free workflows)
      let accessToken = null;
      try {
        accessToken = await getValidAccessToken();
      } catch (e) {
        // Not logged in - okay for free workflows
      }

      const headers = {};
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const response = await fetch(`${API_BASE}/marketplace-download?id=${detailWorkflow.id}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 402) {
          // Insufficient funds - show dialog instead of error
          setInsufficientCreditsDialog({
            isOpen: true,
            required: errorData.required || 0,
            balance: errorData.balance || 0
          });
          return; // Exit early, don't throw
        }
        if (response.status === 403 && errorData.code === 'payment_method_required') {
          // No payment method on file - show modal
          setPaymentMethodModal({ isOpen: true, context: 'download' });
          return;
        }
        throw new Error(errorData.error || 'Failed to download workflow');
      }

      const data = await response.json();
      const workflowFolderName = data.title.replace(/[^a-z0-9_-]/gi, '_').toLowerCase();

      // Check if running in Electron (embedded mode) - use direct install
      if (typeof window !== 'undefined' && window.electronAPI?.installMarketplaceWorkflow) {
        try {
          window.electronAPI.installMarketplaceWorkflow({
            files: data.files,
            workflowName: workflowFolderName
          });

          // Listen for install result
          const handleResult = (event) => {
            const result = event.detail;
            if (result.success) {
              alert(`Successfully installed "${data.title}" to your workflows folder!`);
            } else {
              alert(`Install failed: ${result.error}`);
            }
            window.removeEventListener('electron-install-result', handleResult);
          };
          window.addEventListener('electron-install-result', handleResult);

        } catch (electronError) {
          console.error('Electron install error:', electronError);
          alert(`Install failed: ${electronError.message}`);
        }

      } else {
        // Browser fallback: Create ZIP file
        const zip = new JSZip();
        const workflowFolder = zip.folder(workflowFolderName);

        // Download and add each file to ZIP
        for (const file of data.files) {
          try {
            const fileResponse = await fetch(file.url);
            if (!fileResponse.ok) {
              console.error(`Failed to download ${file.path}`);
              continue;
            }

            const blob = await fileResponse.blob();
            workflowFolder.file(file.path, blob);

          } catch (fileError) {
            console.error(`Error adding ${file.path} to ZIP:`, fileError);
          }
        }

        // Generate and download ZIP
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const zipUrl = window.URL.createObjectURL(zipBlob);
        const link = document.createElement('a');
        link.href = zipUrl;
        link.download = `${workflowFolderName}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(zipUrl);

        // Show OS-specific install instructions
        const isWindows = navigator.platform.includes('Win');
        const installPath = isWindows
          ? 'C:\\Users\\<YourUsername>\\ContextUI\\default\\workflows\\user_workflows\\'
          : '~/ContextUI/default/workflows/user_workflows/';
        alert(`Downloaded "${data.title}" as ZIP file.\n\nExtract it to:\n${installPath}`);
      }

    } catch (error) {
      console.error('Download error:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  if (detailLoading) {
    return (
      <div className="min-h-screen bg-black text-white pt-20 px-4 py-12 flex justify-center items-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-400">Loading workflow...</p>
        </div>
      </div>
    );
  }

  if (detailError || !detailWorkflow) {
    return (
      <div className="min-h-screen bg-black text-white pt-20 px-4 py-12">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/exchange')}
            className="mb-6 flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to Browse
          </button>
          <div className="text-center py-20">
            <p className="text-red-500 mb-2">Workflow not found</p>
            <p className="text-gray-400 text-sm">{detailError}</p>
          </div>
        </div>
      </div>
    );
  }

  const workflow = detailWorkflow;

  // Extract YouTube video ID from URL
  const getYouTubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const videoId = getYouTubeId(workflow.video_url);

  // Get YouTube thumbnail URL
  const getYouTubeThumbnail = (videoId) => {
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  };

  // Build media gallery array
  const mediaGallery = [];

  // Add main thumbnail first
  if (workflow.thumbnail_url) {
    mediaGallery.push({ type: 'image', url: workflow.thumbnail_url, thumbnail: workflow.thumbnail_url });
  }

  // Add screenshots
  if (workflow.screenshot_urls && workflow.screenshot_urls.length > 0) {
    workflow.screenshot_urls.forEach((url) => {
      mediaGallery.push({ type: 'image', url, thumbnail: url });
    });
  }

  // Add YouTube video
  if (videoId) {
    mediaGallery.push({
      type: 'video',
      url: videoId,
      thumbnail: getYouTubeThumbnail(videoId)
    });
  }

  return (
    <>
      <div className={`min-h-screen bg-black text-white ${isEmbedded ? 'pt-4 px-4 py-4' : 'pt-20 px-4 py-12'}`}>
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate(`/exchange${isEmbedded ? '?embedded=true' : ''}`)}
            className="mb-6 flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to Browse
          </button>

          {/* Review Status Banner for Owner */}
          {workflow.is_owner && workflow.review_status === 'pending' && (
            <div className="mb-6 p-4 bg-yellow-900/30 border border-yellow-600/50 rounded-lg flex items-start gap-3">
              <svg className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-yellow-400 font-medium">Pending Review</p>
                <p className="text-yellow-200/70 text-sm">Your workflow is awaiting admin approval before it appears on the public exchange. Only you can see this page.</p>
              </div>
            </div>
          )}
          {workflow.is_owner && workflow.review_status === 'rejected' && (
            <div className="mb-6 p-4 bg-orange-900/30 border border-orange-600/50 rounded-lg flex items-start gap-3">
              <svg className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-orange-400 font-medium">Submission Rejected</p>
                <p className="text-orange-200/70 text-sm">Your workflow was not approved. Please check your feedback in "Published" and make the required changes before resubmitting.</p>
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Main Media Display */}
              <div className={`relative ${selectedMedia.type === 'video' ? 'aspect-video' : 'aspect-[3/4]'} bg-gray-800 rounded-lg overflow-hidden mb-4`}>
                {selectedMedia.type === 'video' ? (
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${selectedMedia.url}`}
                    title={workflow.title}
                    frameBorder="0"
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <img
                    src={selectedMedia.url}
                    alt={workflow.title}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>

              {/* Media Gallery Thumbnails */}
              {mediaGallery.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 mb-6">
                  {mediaGallery.map((media, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedMedia(media)}
                      className={`relative aspect-[3/4] bg-gray-900 rounded-lg cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all ${
                        selectedMedia.url === media.url ? 'ring-2 ring-blue-500' : 'ring-1 ring-gray-800'
                      }`}
                    >
                      <img
                        src={media.thumbnail}
                        alt={`Media ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                      {media.type === 'video' && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg">
                          <Play className="h-6 w-6 text-white" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Title and Creator */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold">{workflow.title}</h1>
                  {workflow.is_isd_ai_creator ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-600/20 border border-emerald-600/50 text-emerald-400 text-xs font-medium rounded-full">
                      <ShieldCheck className="h-3.5 w-3.5" />
                      Official
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-600/20 border border-purple-600/50 text-purple-400 text-xs font-medium rounded-full">
                      Community
                    </span>
                  )}
                </div>
                <p className="text-gray-400">
                  by <span className="text-blue-400">{workflow.creator_display_name || 'Unknown'}</span>
                </p>
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                  <span>⬇ {workflow.downloads_count.toLocaleString()} downloads</span>
                  <span>❤️ {workflow.likes_count?.toLocaleString() || 0} likes</span>
                  {workflow.updated_at && (
                    <span>Updated {new Date(workflow.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  )}
                </div>
              </div>

              {/* Tabs */}
              <div className="mb-6">
                <div className="flex gap-4 border-b border-gray-800">
                  {['description', 'files', 'comments', ...(workflow.fork_tree && (workflow.fork_tree.total_forks > 0 || workflow.fork_tree.total_ancestors > 0) ? ['fork-history'] : [])].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-4 py-2 font-medium capitalize transition-colors ${
                        activeTab === tab
                          ? 'text-blue-400 border-b-2 border-blue-400'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      {tab === 'description' && '📄 Description'}
                      {tab === 'files' && '💻 Files'}
                      {tab === 'comments' && `💬 Comments (${workflow.comments_count || 0})`}
                      {tab === 'fork-history' && '🌳 Fork History'}
                    </button>
                  ))}
                </div>

                {/* Tab Content */}
                <div className="mt-6">
                  {activeTab === 'description' && (
                    <div className="prose prose-invert max-w-none">
                      <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{workflow.description}</p>
                      {workflow.contextui_version_min && (
                        <div className="mt-6 p-4 bg-gray-900 border border-gray-800 rounded-lg">
                          <p className="text-sm text-gray-400">
                            <strong className="text-white">Requires:</strong> ContextUI {workflow.contextui_version_min}+
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'files' && (
                    <div className="bg-gray-900 rounded-lg p-4">
                      {workflow.workflow_files && workflow.workflow_files.length > 0 ? (
                        <>
                          <div className="space-y-1 text-sm font-mono text-gray-400 max-h-96 overflow-y-auto">
                            <div className="text-white mb-3 font-semibold">📁 {workflow.title}/</div>
                            {workflow.workflow_files.map((file, index) => {
                              const depth = (file.path.match(/\//g) || []).length;
                              const fileName = file.path.split('/').pop();
                              const isFolder = file.path.endsWith('/');
                              const padding = depth * 16;

                              return (
                                <div key={index} style={{ paddingLeft: `${padding}px` }} className="hover:bg-gray-800 px-2 py-1 rounded">
                                  {isFolder ? '📁' : '📄'} {fileName}
                                  <span className="text-gray-600 ml-2">
                                    ({(file.size / 1024).toFixed(1)} KB)
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                          <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-700/50 rounded text-sm text-yellow-200">
                            ⚠️ <strong>User-uploaded Python code</strong> - Review code before running. Always inspect files before installing workflows from unknown creators.
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-8 text-gray-400">
                          No files found for this workflow.
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'comments' && (
                    <CommentsSection
                      listingId={workflow.id}
                      user={user}
                      isAuthenticated={isAuthenticated}
                      hasSubscription={hasSubscription}
                      getValidAccessToken={getValidAccessToken}
                      isUserAdmin={isUserAdmin}
                    />
                  )}

                  {activeTab === 'fork-history' && (
                    <div className="space-y-8">
                      {/* Ancestry Section (Parents going UP) */}
                      {workflow.fork_tree && workflow.fork_tree.ancestry && workflow.fork_tree.ancestry.length > 0 && (
                        <div>
                          <h3 className="text-lg font-medium text-white mb-4">Ancestry</h3>
                          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                            <div className="space-y-2">
                              {workflow.fork_tree.ancestry.map((parent, index) => {
                                const statusColor = parent.status === 'published' ? 'text-green-400' : parent.status === 'unpublished' ? 'text-yellow-400' : 'text-red-400';
                                const statusIcon = parent.status === 'published' ? '🟢' : parent.status === 'unpublished' ? '🟡' : '🔴';
                                const isClickable = parent.status === 'published';

                                return (
                                  <div key={parent.id} className="flex items-center gap-2 text-sm" style={{ marginLeft: `${index * 16}px` }}>
                                    <span className={statusColor}>{statusIcon}</span>
                                    {index > 0 && <span className="text-gray-600">└─</span>}
                                    <span className="text-gray-400">Forked from</span>
                                    {isClickable ? (
                                      <button
                                        onClick={() => {
                                          setSelectedWorkflowId(parent.id);
                                          setView('detail');
                                        }}
                                        className="text-blue-400 hover:text-blue-300 font-medium underline"
                                      >
                                        "{parent.title}"
                                      </button>
                                    ) : (
                                      <span className={`font-medium ${parent.status === 'deleted' ? 'line-through text-gray-500' : 'text-gray-300'}`}>
                                        "{parent.title}"
                                      </span>
                                    )}
                                    <span className="text-gray-400">by {parent.creator}</span>
                                    {parent.status !== 'published' && (
                                      <span className="text-xs px-2 py-0.5 rounded bg-gray-800 text-gray-400">
                                        {parent.status}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Forks Section (Children going DOWN) */}
                      {workflow.fork_tree && workflow.fork_tree.forks && workflow.fork_tree.forks.length > 0 && (
                        <div>
                          <h3 className="text-lg font-medium text-white mb-4">
                            Forks ({workflow.fork_tree.total_forks})
                          </h3>
                          <div className="text-gray-400 text-sm mb-4">
                            {workflow.fork_tree.total_forks} workflow{workflow.fork_tree.total_forks > 1 ? 's have' : ' has'} been forked from this:
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {workflow.fork_tree.forks.map((fork) => {
                              const statusColor = fork.status === 'published' ? 'border-green-500/30' : fork.status === 'unpublished' ? 'border-yellow-500/30' : 'border-red-500/30';
                              const statusBadge = fork.status === 'published' ?
                                <span className="px-2 py-0.5 bg-green-900/50 text-green-400 text-xs rounded">Published</span> :
                                fork.status === 'unpublished' ?
                                <span className="px-2 py-0.5 bg-yellow-900/50 text-yellow-400 text-xs rounded">Unpublished</span> :
                                <span className="px-2 py-0.5 bg-red-900/50 text-red-400 text-xs rounded">Deleted</span>;

                              return (
                                <div
                                  key={fork.id}
                                  className={`bg-gray-900 border ${statusColor} rounded-lg p-4 ${fork.status === 'published' ? 'hover:border-blue-500 cursor-pointer' : 'opacity-75'}`}
                                  onClick={() => {
                                    if (fork.status === 'published') {
                                      setSelectedWorkflowId(fork.id);
                                      setView('detail');
                                    }
                                  }}
                                >
                                  <div className="flex justify-between items-start mb-2">
                                    <h4 className={`font-medium ${fork.status === 'deleted' ? 'line-through text-gray-500' : 'text-white'}`}>
                                      {fork.title}
                                    </h4>
                                    {statusBadge}
                                  </div>
                                  <div className="text-sm text-gray-400 mb-2">by {fork.creator}</div>
                                  {fork.status === 'published' && (
                                    <div className="text-xs text-gray-500">
                                      ⬇ {fork.downloads || 0} downloads
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Empty State */}
                      {(!workflow.fork_tree || ((!workflow.fork_tree.ancestry || workflow.fork_tree.ancestry.length === 0) && (!workflow.fork_tree.forks || workflow.fork_tree.forks.length === 0))) && (
                        <div className="text-center py-8 text-gray-400">
                          No fork history available.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-gray-900 rounded-lg p-6 sticky top-24">
                {/* Price Display */}
                {workflow.price_cents > 0 && (
                  <div className="mb-4 text-center">
                    {workflow.user_has_purchased ? (
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-600/20 text-green-400 rounded-full text-sm">
                        <span>✓</span> Purchased
                      </div>
                    ) : workflow.is_isd_ai_creator && subscriptionTier === 'pro' ? (
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-600/20 text-indigo-300 rounded-full text-sm">
                        Free with Pro
                      </div>
                    ) : (
                      <>
                        <div className="text-2xl font-bold text-yellow-400">
                          ${(workflow.price_cents / 100).toFixed(2)}
                        </div>
                        {workflow.is_isd_ai_creator && subscriptionTier !== 'pro' && (
                          <p className="text-xs text-indigo-400 mt-1">Free with Pro subscription</p>
                        )}
                      </>
                    )}
                  </div>
                )}

                {(() => {
                  const isProFreeOfficial = workflow.price_cents > 0 && workflow.is_isd_ai_creator && subscriptionTier === 'pro' && !workflow.user_has_purchased;
                  const isPaid = workflow.price_cents > 0 && !workflow.user_has_purchased && !isProFreeOfficial;
                  return (
                    <button
                      onClick={() => {
                        if (isPaid && !isAuthenticated) {
                          window.location.href = '/';
                        } else {
                          handleDownloadClick();
                        }
                      }}
                      disabled={isDownloading || (isPaid && !isAuthenticated)}
                      className={`w-full px-4 py-3 ${isPaid ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-blue-600 hover:bg-blue-700'} disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors mb-6 flex items-center justify-center gap-2`}
                    >
                      <Download className="h-5 w-5" />
                      {isDownloading ? 'Downloading...'
                        : isProFreeOfficial ? 'Download Free (Pro)'
                        : isPaid
                          ? (!isAuthenticated ? 'Sign In to Buy' : `Buy for $${(workflow.price_cents / 100).toFixed(2)}`)
                          : 'Download Workflow'}
                    </button>
                  );
                })()}

                {/* Like Button */}
                <button
                  onClick={async () => {
                    const wasLiked = likedWorkflows.has(workflow.id);

                    // Optimistic update for detail view only
                    setDetailWorkflow(prev => ({
                      ...prev,
                      likes_count: wasLiked ? (prev.likes_count || 1) - 1 : (prev.likes_count || 0) + 1
                    }));

                    // Call the shared handler (updates global state and makes API call)
                    await handleLike(workflow.id);
                  }}
                  className={`w-full px-4 py-3 rounded-lg font-medium transition-all mb-6 flex items-center justify-center gap-2 ${
                    likedWorkflows.has(workflow.id)
                      ? 'bg-red-600/20 hover:bg-red-600/30 border-2 border-red-600/50 text-red-400'
                      : 'bg-gray-800 hover:bg-gray-700 border-2 border-gray-700 text-gray-300'
                  }`}
                  disabled={!isAuthenticated}
                >
                  <Heart className={`h-5 w-5 ${likedWorkflows.has(workflow.id) ? 'fill-red-500' : ''}`} />
                  {likedWorkflows.has(workflow.id) ? 'Liked' : 'Like'} ({workflow.likes_count?.toLocaleString() || 0})
                </button>

                <div className="space-y-4 text-sm">
                  <div>
                    <div className="text-gray-500 mb-1">Category</div>
                    <div className="text-white font-medium">{workflow.category}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 mb-1">Compatible With</div>
                    <div className="text-white font-medium">ContextUI {workflow.contextui_version_min}+</div>
                  </div>
                  {workflow.updated_at && (
                    <div>
                      <div className="text-gray-500 mb-1">Last Updated</div>
                      <div className="text-white font-medium">
                        {new Date(workflow.updated_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Edit & Delete Buttons - Show for creator or admin */}
                {isAuthenticated && detailWorkflow && (detailWorkflow.is_owner || isUserAdmin) && (
                  <div className="mt-6 pt-6 border-t border-gray-800 space-y-3">
                    {/* Edit Button - Only for owner */}
                    {detailWorkflow.is_owner && (
                      <button
                        onClick={() => {
                          setEditingWorkflowId(detailWorkflow.id);
                          setView('upload');
                        }}
                        className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        <Edit className="h-4 w-4" />
                        Edit Workflow
                      </button>
                    )}

                    {/* Delete Button - For creator or admin */}
                    <button
                      onClick={handleDeleteWorkflow}
                      disabled={isDeleting}
                      className="w-full px-4 py-2 bg-red-600/10 hover:bg-red-600/20 border border-red-600/50 text-red-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      {isDeleting ? 'Deleting...' : 'Delete Workflow'}
                    </button>
                    {isUserAdmin && !detailWorkflow.is_owner && (
                      <p className="text-xs text-yellow-500 mt-2 text-center">
                        ⚠️ Admin action
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Purchase Confirmation Dialog */}
      {purchaseConfirmDialog.isOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl max-w-md w-full border border-gray-800">
            <div className="p-6 border-b border-gray-800">
              <h2 className="text-xl font-bold text-white">Confirm Purchase</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-400 mb-2">
                  ${(purchaseConfirmDialog.priceCents / 100).toFixed(2)}
                </div>
                <p className="text-gray-400">
                  This amount will be deducted from your credits.
                </p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400">Your current balance:</span>
                  <span className="text-white font-medium">${(purchaseConfirmDialog.balanceCents / 100).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center text-sm mt-2">
                  <span className="text-gray-400">After purchase:</span>
                  <span className="text-green-400 font-medium">${((purchaseConfirmDialog.balanceCents - purchaseConfirmDialog.priceCents) / 100).toFixed(2)}</span>
                </div>
              </div>
              <p className="text-sm text-gray-500 text-center">
                70% of this purchase goes directly to the creator.
              </p>
            </div>
            <div className="p-6 border-t border-gray-800 flex gap-3">
              <button
                onClick={() => setPurchaseConfirmDialog({ isOpen: false, priceCents: 0, balanceCents: 0 })}
                className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setPurchaseConfirmDialog({ isOpen: false, priceCents: 0, balanceCents: 0 });
                  handleDownload();
                }}
                className="flex-1 px-4 py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium transition-colors"
              >
                Continue to Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Insufficient Credits Dialog */}
      {insufficientCreditsDialog.isOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl max-w-md w-full border border-gray-800">
            <div className="p-6 border-b border-gray-800">
              <h2 className="text-xl font-bold text-red-400">Insufficient Credits</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-400 mb-2">
                  ${(insufficientCreditsDialog.required / 100).toFixed(2)}
                </div>
                <p className="text-gray-400">
                  This workflow costs ${(insufficientCreditsDialog.required / 100).toFixed(2)}
                </p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400">Your current balance:</span>
                  <span className="text-white font-medium">${(insufficientCreditsDialog.balance / 100).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center text-sm mt-2">
                  <span className="text-gray-400">You need:</span>
                  <span className="text-red-400 font-medium">${((insufficientCreditsDialog.required - insufficientCreditsDialog.balance) / 100).toFixed(2)} more</span>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-800 space-y-4">
              {/* Option 1: Direct pay */}
              {insufficientCreditsDialog.paymentMethod && (
                <div>
                  <p className="text-sm text-gray-400 mb-2">Option 1: Pay now</p>
                  <button
                    onClick={handleDirectPurchase}
                    disabled={isDirectPaying}
                    className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    {isDirectPaying ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        Charge ${(insufficientCreditsDialog.required / 100).toFixed(2)} to {insufficientCreditsDialog.paymentMethod.brand} •••{insufficientCreditsDialog.paymentMethod.last4}
                      </>
                    )}
                  </button>
                </div>
              )}
              
              {/* Divider */}
              {insufficientCreditsDialog.paymentMethod && (
                <div className="flex items-center gap-3">
                  <div className="flex-1 border-t border-gray-700"></div>
                  <span className="text-gray-500 text-sm">or</span>
                  <div className="flex-1 border-t border-gray-700"></div>
                </div>
              )}
              
              {/* Option 2: Add credits */}
              <div>
                <p className="text-sm text-gray-400 mb-2">Option 2: Top up credit balance</p>
                <button
                  onClick={() => {
                    setInsufficientCreditsDialog({ isOpen: false, required: 0, balance: 0, paymentMethod: null });
                    navigate('/account?tab=credits');
                  }}
                  className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  Add Credits to Account
                </button>
              </div>
              
              {/* Cancel */}
              <button
                onClick={() => setInsufficientCreditsDialog({ isOpen: false, required: 0, balance: 0, paymentMethod: null })}
                className="w-full px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm"
              >
                Cancel
              </button>
              
              <p className="text-xs text-gray-500 text-center">
                70% of purchases go directly to the creator
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Add Payment Method Modal */}
      <AddPaymentMethodModal
        isOpen={paymentMethodModal.isOpen}
        onClose={() => setPaymentMethodModal({ isOpen: false, context: 'download' })}
        onSuccess={() => {
          // After adding payment method, retry the download click
          handleDownloadClick();
        }}
        context={paymentMethodModal.context}
      />
    </>
  );
};

// ============================================
// BROWSE VIEW COMPONENT (Extracted to prevent re-creation on parent re-renders)
// ============================================
const BrowseView = ({
  workflows,
  loading,
  error,
  pagination,
  setPagination,
  searchQuery,
  setSearchQuery,
  selectedCategory,
  setSelectedCategory,
  sortBy,
  setSortBy,
  isAuthenticated,
  hasSubscription,
  setView,
  setSelectedWorkflowId,
  fetchWorkflows,
  fetchMyWorkflows,
  fetchMyDownloads,
  handleLike,
  likedWorkflows,
  navigate,
  isEmbedded = false,
  isUserAdmin = false,
  fetchReviewQueue,
  fetchDeletedWorkflows,
  sourceFilter,
  setSourceFilter,
  subscriptionTier,
  successBanner,
  setSuccessBanner,
  getValidAccessToken,
}) => {
  const [showSyncPanel, setShowSyncPanel] = useState(false);

  const handleWorkflowClick = (id) => {
    navigate(`/exchange/${id}${isEmbedded ? '?embedded=true' : ''}`);
  };

  return (
    <div className={`min-h-screen bg-black text-white ${isEmbedded ? 'pt-4 px-4 py-4' : 'pt-20 px-4 py-12'}`}>
      <div className="max-w-7xl mx-auto">
        {/* Sync Panel Modal */}
        <SyncPanel
          isOpen={showSyncPanel}
          onClose={() => setShowSyncPanel(false)}
          getValidAccessToken={getValidAccessToken}
          onSyncComplete={fetchMyWorkflows}
        />

        {/* Success Banner (e.g. after private workflow upload) */}
        {successBanner && (
          <div className="mb-6 p-4 rounded-lg border flex items-start justify-between bg-green-900/30 border-green-600">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 text-green-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-green-300">
                  {successBanner.type === 'published_private'
                    ? 'Private Workflow Published'
                    : 'Workflow Updated Successfully'}
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  {successBanner.type === 'published_private'
                    ? `"${successBanner.workflowTitle}" is now available in your private workflows.`
                    : `"${successBanner.workflowTitle}" has been updated successfully.`}
                </p>
              </div>
            </div>
            <button
              onClick={() => setSuccessBanner(null)}
              className="text-gray-500 hover:text-gray-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Header - hidden when embedded */}
        {!isEmbedded && (
          <div className="mb-8">
            <h1 className="text-4xl font-bold">ContextUI Exchange</h1>
          </div>
        )}

        {/* Primary Tabs */}
        <div className="mb-6 flex flex-wrap gap-3">
          <button
            onClick={() => setView('browse')}
            className="px-4 py-2 rounded-lg font-medium transition-colors bg-blue-600 text-white"
          >
            Workflows
          </button>
          <button
            onClick={() => setView('comments-log')}
            className="px-4 py-2 rounded-lg font-medium transition-colors bg-gray-900 text-gray-400 hover:bg-gray-800"
          >
            Comments
          </button>
          {isAuthenticated && (
            <>
              <button
                onClick={() => {
                  setView('my-workflows');
                  fetchMyWorkflows();
                }}
                className="px-4 py-2 rounded-lg font-medium transition-colors bg-gray-900 text-gray-400 hover:bg-gray-800"
              >
                Published
              </button>
              <button
                onClick={() => {
                  setView('my-downloads');
                  fetchMyDownloads();
                }}
                className="px-4 py-2 rounded-lg font-medium transition-colors bg-gray-900 text-gray-400 hover:bg-gray-800"
              >
                My Downloads
              </button>
              {isUserAdmin && (
                <>
                  <button
                    onClick={() => {
                      setView('review-queue');
                      fetchReviewQueue();
                    }}
                    className="px-4 py-2 rounded-lg font-medium transition-colors bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    Review Queue
                  </button>
                  <button
                    onClick={() => {
                      setView('deleted-workflows');
                      fetchDeletedWorkflows();
                    }}
                    className="px-4 py-2 rounded-lg font-medium transition-colors bg-red-600 hover:bg-red-700 text-white"
                  >
                    Deleted
                  </button>
                </>
              )}
            </>
          )}
        </div>

        {/* Official / Community Toggle */}
        <div className="mb-4 flex items-center gap-2">
          <button
            onClick={() => {
              setSourceFilter('official');
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              sourceFilter === 'official'
                ? 'bg-emerald-600 text-white'
                : 'bg-gray-900 text-gray-400 hover:bg-gray-800'
            }`}
          >
            <ShieldCheck className="h-4 w-4" />
            Official
          </button>
          <button
            onClick={() => {
              setSourceFilter('community');
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              sourceFilter === 'community'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-900 text-gray-400 hover:bg-gray-800'
            }`}
          >
            Community
          </button>
          {isAuthenticated && (
            <button
              onClick={() => {
                if (subscriptionTier === 'pro') {
                  setSourceFilter('private');
                  setPagination(prev => ({ ...prev, page: 1 }));
                }
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                sourceFilter === 'private'
                  ? 'bg-indigo-600 text-white'
                  : subscriptionTier === 'pro'
                    ? 'bg-gray-900 text-gray-400 hover:bg-gray-800'
                    : 'bg-gray-900 text-gray-600 cursor-not-allowed'
              }`}
              title={subscriptionTier !== 'pro' ? 'Pro subscription required' : ''}
            >
              <Lock className="h-4 w-4" />
              Private
              {subscriptionTier !== 'pro' && (
                <span className="text-[10px] bg-indigo-600 text-white px-1.5 py-0.5 rounded-full font-bold">PRO</span>
              )}
            </button>
          )}
          {/* Sync button - only visible for Pro users in Electron with Private tab active */}
          {sourceFilter === 'private' && subscriptionTier === 'pro' && typeof window !== 'undefined' && window.electronAPI && (
            <button
              onClick={() => setShowSyncPanel(true)}
              className="px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 bg-gray-900 text-gray-400 hover:bg-gray-800 ml-auto"
              title="Sync your local workflows folder with the private exchange"
            >
              <RefreshCw className="h-4 w-4" />
              Sync
            </button>
          )}
        </div>

        {/* Trust / Caution Banners */}
        {sourceFilter === 'official' && (
          <div className="mb-6 p-4 bg-emerald-900/20 border border-emerald-700/50 rounded-lg flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-emerald-200 font-medium text-sm">Official Workflows</p>
              <p className="text-emerald-200/70 text-sm mt-1">
                Published by the ContextUI team. You can trust these workflows are safe to use.
              </p>
            </div>
          </div>
        )}
        {sourceFilter === 'community' && (
          <div className="mb-6 p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-yellow-200 font-medium text-sm">Community Workflows</p>
              <p className="text-yellow-200/70 text-sm mt-1">
                Created by community members. Review workflow contents before running from unknown creators.
              </p>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="mb-8 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={sourceFilter === 'official' ? 'Search official workflows...' : 'Search community workflows...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Category Tabs + Sort + Upload Button */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2 items-center">
              {MAIN_CATEGORIES.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-900 text-gray-400 hover:bg-gray-800'
                  }`}
                >
                  {category.label}
                </button>
              ))}

              {/* More categories dropdown */}
              <select
                value={MORE_CATEGORIES.find(c => c.id === selectedCategory) ? selectedCategory : ''}
                onChange={(e) => e.target.value && setSelectedCategory(e.target.value)}
                className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">More...</option>
                {MORE_CATEGORIES.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort + Upload */}
            <div className="flex items-center gap-3">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>

              <button
                onClick={() => {
                  if (!isAuthenticated) {
                    window.location.href = '/';
                  } else {
                    setView('upload');
                  }
                }}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5"
              >
                <Upload className="h-4 w-4" />
                {!isAuthenticated ? 'Sign In to Upload' : 'Upload'}
              </button>
            </div>
          </div>
        </div>

        {/* Workflows Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
              <p className="mt-4 text-gray-400">Loading workflows...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <p className="text-red-500 mb-2">Error loading workflows</p>
              <p className="text-gray-400 text-sm">{error}</p>
              <button
                onClick={fetchWorkflows}
                className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : workflows.length === 0 ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <p className="text-gray-400 text-lg mb-2">No workflows found</p>
              <p className="text-gray-500 text-sm">
                {searchQuery
                  ? 'Try a different search term'
                  : sourceFilter === 'private'
                    ? 'Upload a workflow with Private visibility to see it here.'
                    : 'Be the first to upload a workflow!'}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {workflows.map((workflow) => (
                <WorkflowCard
                  key={workflow.id}
                  workflow={workflow}
                  onClick={handleWorkflowClick}
                  onLike={handleLike}
                  isLiked={likedWorkflows.has(workflow.id)}
                />
              ))}
            </div>

            {/* Pagination Info */}
            {pagination.total > 0 && (
              <div className="mt-8 text-center text-gray-400 text-sm">
                Showing {workflows.length} of {pagination.total} workflows
              </div>
            )}

            {/* Pagination Controls */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-6">
                <button
                  onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-gray-400">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                  disabled={pagination.page >= pagination.totalPages}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
};

// ============================================
// MY WORKFLOWS VIEW COMPONENT (Extracted to prevent re-creation on parent re-renders)
// ============================================
const MyWorkflowsView = ({
  myWorkflows,
  myWorkflowsLoading,
  myWorkflowsError,
  setView,
  setSelectedWorkflowId,
  setEditingWorkflowId,
  fetchMyWorkflows,
  fetchMyDownloads,
  navigate,
  isEmbedded = false,
  getValidAccessToken,
  successBanner,
  setSuccessBanner,
  subscriptionTier,
}) => {
  const [hardDeleting, setHardDeleting] = useState(null);
  const [feedbackWorkflow, setFeedbackWorkflow] = useState(null);
  const [feedbackAccessToken, setFeedbackAccessToken] = useState(null);

  const openFeedbackPanel = async (workflow) => {
    try {
      const token = await getValidAccessToken();
      setFeedbackAccessToken(token);
      setFeedbackWorkflow(workflow);
    } catch (err) {
      console.error('Failed to get access token:', err);
      alert('Failed to open feedback panel. Please try again.');
    }
  };

  const handleWorkflowClick = (id) => {
    navigate(`/exchange/${id}${isEmbedded ? '?embedded=true' : ''}`);
  };

  const handleEditWorkflow = (workflow) => {
    setEditingWorkflowId(workflow.id);
    setView('upload');
  };

  const handleHardDelete = async (workflow) => {
    if (!confirm(`Are you sure you want to PERMANENTLY delete "${workflow.title}"?\n\nThis will remove all files from storage and cannot be undone.`)) {
      return;
    }

    setHardDeleting(workflow.id);
    try {
      const accessToken = await getValidAccessToken();
      const response = await fetch(`${API_BASE}/marketplace-delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ listingId: workflow.id, hardDelete: true })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete workflow');
      }

      // Refresh the list
      fetchMyWorkflows();
    } catch (err) {
      console.error('Hard delete error:', err);
      alert('Failed to permanently delete: ' + err.message);
    } finally {
      setHardDeleting(null);
    }
  };

  const getStatusBadge = (workflow) => {
    if (workflow.deleted_at) {
      return <span className="px-2 py-1 text-xs font-medium bg-red-900/50 text-red-400 rounded">Deleted</span>;
    } else if (workflow.review_status === 'pending') {
      return (
        <span
          className="px-2 py-1 text-xs font-medium bg-yellow-900/50 text-yellow-400 rounded flex items-center gap-1 cursor-help"
          title="Your workflow is awaiting admin approval before it appears on the public exchange"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Pending Review
        </span>
      );
    } else if (workflow.review_status === 'rejected') {
      return (
        <div className="flex items-center gap-1">
          <span
            className="px-2 py-1 text-xs font-medium bg-orange-900/50 text-orange-400 rounded cursor-help"
            title={workflow.review_notes || "Your workflow was not approved. Please edit and resubmit."}
          >
            Rejected
          </span>
          {workflow.unread_feedback_count > 0 && (
            <span className="px-1.5 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full">
              {workflow.unread_feedback_count}
            </span>
          )}
        </div>
      );
    } else if (!workflow.is_published) {
      return <span className="px-2 py-1 text-xs font-medium bg-yellow-900/50 text-yellow-400 rounded">Unpublished</span>;
    } else {
      return <span className="px-2 py-1 text-xs font-medium bg-green-900/50 text-green-400 rounded">Published</span>;
    }
  };

  // Calculate stats (excluding deleted workflows)
  const totalWorkflows = myWorkflows.filter(w => !w.deleted_at).length;
  const totalDownloads = myWorkflows.reduce((sum, w) => sum + (w.downloads_count || 0), 0);
  const totalLikes = myWorkflows.reduce((sum, w) => sum + (w.likes_count || 0), 0);

  return (
    <div className={`min-h-screen bg-black text-white ${isEmbedded ? 'pt-4 px-4 py-4' : 'pt-20 px-4 py-12'}`}>
      <div className="max-w-7xl mx-auto">
        {/* Header - hidden when embedded */}
        {!isEmbedded && (
          <div className="mb-8">
            <h1 className="text-4xl font-bold">ContextUI Exchange</h1>
          </div>
        )}

        {/* Primary Tabs */}
        <div className="mb-6 flex flex-wrap gap-3">
          <button
            onClick={() => setView('browse')}
            className="px-4 py-2 rounded-lg font-medium transition-colors bg-gray-900 text-gray-400 hover:bg-gray-800"
          >
            Workflows
          </button>
          <button
            onClick={() => setView('comments-log')}
            className="px-4 py-2 rounded-lg font-medium transition-colors bg-gray-900 text-gray-400 hover:bg-gray-800"
          >
            Comments
          </button>
          <button
            onClick={() => setView('my-workflows')}
            className="px-4 py-2 rounded-lg font-medium transition-colors bg-blue-600 text-white"
          >
            Published
          </button>
          <button
            onClick={() => {
              setView('my-downloads');
              fetchMyDownloads();
            }}
            className="px-4 py-2 rounded-lg font-medium transition-colors bg-gray-900 text-gray-400 hover:bg-gray-800"
          >
            My Downloads
          </button>
        </div>

        {/* Success Banner */}
        {successBanner && (
          <div className={`mb-6 p-4 rounded-lg border flex items-start justify-between ${
            successBanner.type === 'updated'
              ? 'bg-green-900/30 border-green-600'
              : 'bg-blue-900/30 border-blue-600'
          }`}>
            <div className="flex items-start gap-3">
              <div className={`mt-0.5 ${successBanner.type === 'updated' ? 'text-green-400' : 'text-blue-400'}`}>
                {successBanner.type === 'updated' ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
              <div>
                <p className={`font-medium ${successBanner.type === 'updated' ? 'text-green-300' : 'text-blue-300'}`}>
                  {successBanner.type === 'submitted'
                    ? 'Workflow Submitted for Review'
                    : successBanner.type === 'resubmitted'
                    ? 'Workflow Resubmitted for Review'
                    : 'Workflow Updated Successfully'}
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  {successBanner.type === 'submitted'
                    ? `"${successBanner.workflowTitle}" has been submitted and is pending approval. You'll see a "Pending Review" badge below until an admin approves it for the public exchange.`
                    : successBanner.type === 'resubmitted'
                    ? `"${successBanner.workflowTitle}" has been updated with new files and is pending re-approval. You'll see a "Pending Review" badge below until an admin approves the changes.`
                    : `"${successBanner.workflowTitle}" has been updated successfully.`}
                </p>
              </div>
            </div>
            <button
              onClick={() => setSuccessBanner(null)}
              className="text-gray-500 hover:text-gray-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Stats Summary Card */}
        {myWorkflows.length > 0 && (
          <div className="mb-8 bg-gray-900 border border-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Your Workflows</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-3xl font-bold text-blue-400">{totalWorkflows}</div>
                <div className="text-sm text-gray-400">Workflows</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-gray-400">{totalDownloads.toLocaleString()}</div>
                <div className="text-sm text-gray-400">Downloads</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-red-400">{totalLikes.toLocaleString()}</div>
                <div className="text-sm text-gray-400">Likes</div>
              </div>
            </div>
          </div>
        )}

        {/* Submit for Review Button */}
        <div className="mb-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold">Manage Your Workflows</h2>
          <button
            onClick={() => {
              setEditingWorkflowId(null);
              setView('upload');
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <Upload className="h-4 w-4" />
            Submit for Review
          </button>
        </div>

        {/* Workflows Grid */}
        {myWorkflowsLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
              <p className="mt-4 text-gray-400">Loading your workflows...</p>
            </div>
          </div>
        ) : myWorkflowsError ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <p className="text-red-500 mb-2">Error loading workflows</p>
              <p className="text-gray-400 text-sm">{myWorkflowsError}</p>
              <button
                onClick={fetchMyWorkflows}
                className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : myWorkflows.length === 0 ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <p className="text-gray-400 text-lg mb-2">You haven't submitted any workflows yet</p>
              <p className="text-gray-500 text-sm mb-6">Share your work with the community! Submissions are reviewed by admins before going live.</p>
              <button
                onClick={() => {
                  setEditingWorkflowId(null);
                  setView('upload');
                }}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors inline-flex items-center gap-2"
              >
                <Upload className="h-5 w-5" />
                Submit Your First Workflow
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {myWorkflows.map((workflow) => (
              <div key={workflow.id} className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden hover:border-blue-500 transition-all">
                {/* Thumbnail */}
                <div
                  onClick={() => handleWorkflowClick(workflow.id)}
                  className="relative aspect-[3/4] bg-gray-800 cursor-pointer group overflow-hidden"
                >
                  {workflow.thumbnail_url ? (
                    <>
                      <img
                        src={workflow.thumbnail_url}
                        alt={workflow.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                      <ImageIcon className="h-12 w-12 text-gray-600" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    </div>
                  )}
                  {/* Status Badge Overlay */}
                  <div className="absolute top-2 right-2 flex gap-1 z-10">
                    {getStatusBadge(workflow)}
                  </div>
                  {/* Title overlay at bottom */}
                  <div className="absolute bottom-0 left-0 right-0 p-3 z-10">
                    <h3 className="text-sm font-semibold text-white drop-shadow-lg line-clamp-2">{workflow.title}</h3>
                  </div>
                </div>

                {/* Content */}
                <div className="p-3">
                  <p className="text-sm text-gray-400 mb-3 line-clamp-2">{workflow.description}</p>

                  {/* Stats */}
                  <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                    <span>⬇ {workflow.downloads_count.toLocaleString()} downloads</span>
                    <span>❤️ {workflow.likes_count?.toLocaleString() || 0} likes</span>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => handleEditWorkflow(workflow)}
                      className="flex-1 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded text-sm font-medium transition-colors flex items-center justify-center gap-1.5"
                    >
                      <Edit className="h-4 w-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleWorkflowClick(workflow.id)}
                      className="px-3 py-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded text-sm font-medium transition-colors"
                    >
                      View
                    </button>
                    {workflow.review_status === 'rejected' && (
                      <button
                        onClick={() => openFeedbackPanel(workflow)}
                        className="px-3 py-2 bg-orange-600/20 hover:bg-orange-600/30 text-orange-400 rounded text-sm font-medium transition-colors flex items-center gap-1"
                      >
                        <MessageSquare className="h-4 w-4" />
                        Feedback
                        {workflow.unread_feedback_count > 0 && (
                          <span className="px-1 py-0.5 text-xs bg-red-500 text-white rounded-full min-w-[18px] text-center">
                            {workflow.unread_feedback_count}
                          </span>
                        )}
                      </button>
                    )}
                    {workflow.deleted_at && (
                      <button
                        onClick={() => handleHardDelete(workflow)}
                        disabled={hardDeleting === workflow.id}
                        className="px-3 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded text-sm font-medium transition-colors flex items-center gap-1 disabled:opacity-50"
                        title="Permanently delete"
                      >
                        <Trash2 className="h-4 w-4" />
                        {hardDeleting === workflow.id ? '...' : ''}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Feedback Panel for rejected workflows */}
      <FeedbackPanel
        listingId={feedbackWorkflow?.id}
        listingTitle={feedbackWorkflow?.title}
        isOpen={!!feedbackWorkflow}
        onClose={() => {
          setFeedbackWorkflow(null);
          setFeedbackAccessToken(null);
          fetchMyWorkflows(); // Refresh to update unread counts
        }}
        accessToken={feedbackAccessToken}
      />
    </div>
  );
};

// ============================================
// MY DOWNLOADS VIEW COMPONENT (Extracted to prevent re-creation on parent re-renders)
// ============================================
const MyDownloadsView = ({
  myDownloads,
  myDownloadsLoading,
  myDownloadsError,
  setView,
  setSelectedWorkflowId,
  fetchMyWorkflows,
  fetchMyDownloads,
  navigate,
  isEmbedded = false
}) => {
  const handleWorkflowClick = (id) => {
    navigate(`/exchange/${id}${isEmbedded ? '?embedded=true' : ''}`);
  };

  const handleRedownload = async (downloadId) => {
    // Trigger re-download by navigating to detail view and triggering download
    navigate(`/exchange/${downloadId}${isEmbedded ? '?embedded=true' : ''}`);
    // The download will be triggered from the detail view
  };

  const totalUpdates = myDownloads.filter(d => d.has_update).length;

  return (
    <div className={`min-h-screen bg-black text-white ${isEmbedded ? 'pt-4 px-4 py-4' : 'pt-20 px-4 py-12'}`}>
      <div className="max-w-7xl mx-auto">
        {/* Header - hidden when embedded */}
        {!isEmbedded && (
          <div className="mb-8">
            <h1 className="text-4xl font-bold">ContextUI Exchange</h1>
          </div>
        )}

        {/* Primary Tabs */}
        <div className="mb-6 flex flex-wrap gap-3">
          <button
            onClick={() => setView('browse')}
            className="px-4 py-2 rounded-lg font-medium transition-colors bg-gray-900 text-gray-400 hover:bg-gray-800"
          >
            Workflows
          </button>
          <button
            onClick={() => setView('comments-log')}
            className="px-4 py-2 rounded-lg font-medium transition-colors bg-gray-900 text-gray-400 hover:bg-gray-800"
          >
            Comments
          </button>
          <button
            onClick={() => {
              setView('my-workflows');
              fetchMyWorkflows();
            }}
            className="px-4 py-2 rounded-lg font-medium transition-colors bg-gray-900 text-gray-400 hover:bg-gray-800"
          >
            Published
          </button>
          <button
            onClick={() => setView('my-downloads')}
            className="px-4 py-2 rounded-lg font-medium transition-colors bg-blue-600 text-white"
          >
            My Downloads
          </button>
        </div>

        {/* Update Notification Banner */}
        {totalUpdates > 0 && (
          <div className="mb-6 bg-blue-900/30 border border-blue-600 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="text-blue-400 text-lg">🔔</div>
              <div>
                <div className="font-medium">
                  {totalUpdates} workflow{totalUpdates > 1 ? 's have' : ' has'} updates available
                </div>
                <div className="text-sm text-gray-400">
                  New versions are ready to download
                </div>
              </div>
            </div>
          </div>
        )}

        <h2 className="text-2xl font-bold mb-6">Your Downloads ({myDownloads.length})</h2>

        {/* Downloads Grid */}
        {myDownloadsLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
              <p className="mt-4 text-gray-400">Loading your downloads...</p>
            </div>
          </div>
        ) : myDownloadsError ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <p className="text-red-500 mb-2">Error loading downloads</p>
              <p className="text-gray-400 text-sm">{myDownloadsError}</p>
              <button
                onClick={fetchMyDownloads}
                className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : myDownloads.length === 0 ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <p className="text-gray-400 text-lg mb-2">You haven't downloaded any workflows yet</p>
              <p className="text-gray-500 text-sm mb-6">Browse the marketplace to find workflows to download</p>
              <button
                onClick={() => setView('browse')}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Browse Marketplace
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {myDownloads.map((download) => (
              <div key={download.id} className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden hover:border-blue-500 transition-all">
                {/* Thumbnail */}
                <div
                  onClick={() => handleWorkflowClick(download.id)}
                  className="relative aspect-[3/4] bg-gray-800 cursor-pointer group overflow-hidden"
                >
                  {download.thumbnail_url ? (
                    <>
                      <img
                        src={download.thumbnail_url}
                        alt={download.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                      <ImageIcon className="h-12 w-12 text-gray-600" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    </div>
                  )}
                  {/* Update notification badge */}
                  {download.has_update && (
                    <div className="absolute top-2 right-2 bg-blue-600 text-white text-xs px-2 py-1 rounded font-medium z-10">
                      Update Available
                    </div>
                  )}
                  {/* Title overlay at bottom */}
                  <div className="absolute bottom-0 left-0 right-0 p-3 z-10">
                    <h3 className="text-sm font-semibold text-white drop-shadow-lg line-clamp-2">{download.title}</h3>
                  </div>
                </div>

                {/* Content */}
                <div className="p-3">
                  <h3 className="font-semibold text-white mb-1 line-clamp-1">{download.title}</h3>
                  <p className="text-sm text-gray-400 mb-2">by {download.creator}</p>

                  {/* Download info */}
                  <div className="text-xs text-gray-500 mb-3">
                    <div>Downloaded {new Date(download.downloaded_at).toLocaleDateString()}</div>
                    {download.has_update ? (
                      <div className="text-blue-400 font-medium">v{download.downloaded_version} → v{download.latest_version} available</div>
                    ) : (
                      <div>v{download.downloaded_version} (up to date)</div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleWorkflowClick(download.id)}
                      className="flex-1 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded text-sm font-medium transition-colors"
                    >
                      View
                    </button>
                    {download.has_update && (
                      <button
                        onClick={() => handleRedownload(download.id)}
                        className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium transition-colors flex items-center gap-1.5"
                      >
                        <Download className="h-3.5 w-3.5" />
                        Update
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// FILE TREE NODE COMPONENT
// ============================================
const FileTreeNode = ({ node, selectedIndices, onToggleFile, onToggleFolder, expandedFolders, setExpandedFolders, depth = 0 }) => {
  // Check if this folder or any descendant is selected
  const getFolderSelectionState = (node) => {
    const allFileIndices = [];
    const collectIndices = (n) => {
      n.files.forEach(f => allFileIndices.push(f.index));
      Object.values(n.children).forEach(collectIndices);
    };
    collectIndices(node);

    const selectedCount = allFileIndices.filter(i => selectedIndices.has(i)).length;
    return {
      allSelected: selectedCount === allFileIndices.length && allFileIndices.length > 0,
      someSelected: selectedCount > 0 && selectedCount < allFileIndices.length,
      noneSelected: selectedCount === 0,
      allIndices: allFileIndices,
    };
  };

  const handleFolderCheckboxChange = (node) => {
    const state = getFolderSelectionState(node);
    onToggleFolder(state.allIndices, !state.allSelected);
  };

  const toggleExpand = (path) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedFolders(newExpanded);
  };

  const folderNames = Object.keys(node.children).sort();
  const sortedFiles = [...node.files].sort((a, b) => a.name.localeCompare(b.name));

  if (node.type === 'folder' && folderNames.length === 0 && sortedFiles.length === 0) {
    return null;
  }

  return (
    <div>
      {/* Folders */}
      {folderNames.map(folderName => {
        const childNode = node.children[folderName];
        const childFolderState = getFolderSelectionState(childNode);
        const isExpanded = expandedFolders.has(childNode.path);

        return (
          <div key={folderName}>
            <div
              className={`flex items-center gap-2 p-2 hover:bg-gray-800/50 rounded`}
              style={{ paddingLeft: `${depth * 1}rem` }}
            >
              {/* Folder Checkbox */}
              <input
                type="checkbox"
                checked={childFolderState.allSelected}
                ref={input => {
                  if (input) {
                    input.indeterminate = childFolderState.someSelected;
                  }
                }}
                onChange={() => handleFolderCheckboxChange(childNode)}
                className="h-4 w-4 rounded border-gray-600 bg-gray-800 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-900"
              />

              {/* Expand/Collapse Icon */}
              <button
                type="button"
                onClick={() => toggleExpand(childNode.path)}
                className="text-gray-400 hover:text-gray-300 focus:outline-none"
              >
                {isExpanded ? (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                )}
              </button>

              {/* Folder Icon and Name */}
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-yellow-500">📁</span>
                <span className="text-sm font-medium text-gray-300 truncate">
                  {folderName}
                </span>
                <span className="text-xs text-gray-600">
                  ({childFolderState.allIndices.length} files)
                </span>
              </div>
            </div>

            {/* Render children if expanded */}
            {isExpanded && (
              <FileTreeNode
                node={childNode}
                selectedIndices={selectedIndices}
                onToggleFile={onToggleFile}
                onToggleFolder={onToggleFolder}
                expandedFolders={expandedFolders}
                setExpandedFolders={setExpandedFolders}
                depth={depth + 1}
              />
            )}
          </div>
        );
      })}

      {/* Files */}
      {sortedFiles.map(fileObj => (
        <label
          key={fileObj.index}
          className={`flex items-center gap-2 p-2 hover:bg-gray-800/50 rounded cursor-pointer group`}
          style={{ paddingLeft: `${depth * 1 + 1.5}rem` }}
        >
          <input
            type="checkbox"
            checked={selectedIndices.has(fileObj.index)}
            onChange={() => onToggleFile(fileObj.index)}
            className="h-4 w-4 rounded border-gray-600 bg-gray-800 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-900"
          />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-mono text-gray-400 truncate group-hover:text-gray-300">
              📄 {fileObj.name}
            </p>
            <p className="text-xs text-gray-600">
              {(fileObj.file.size / 1024).toFixed(1)} KB
            </p>
          </div>
        </label>
      ))}
    </div>
  );
};

// ============================================
// FILE FILTER CONSTANTS
// ============================================
const EXCLUDED_FOLDER_NAMES = ['venv', '__pycache__', 'node_modules', '.git', '.vscode', '.idea', '.env', 'env', '.venv'];
// Blacklist approach - block installers/executables and compiled files
const BLOCKED_EXTENSIONS = ['.exe', '.msi', '.dmg', '.pkg', '.app', '.deb', '.rpm', '.appimage', '.apk', '.ipa', '.pyc', '.pyo', '.pyd', '.so', '.dll', '.dylib'];
const SYSTEM_FILES = ['.DS_Store', 'Thumbs.db', 'desktop.ini'];

// Helper to check if a path should be excluded
const shouldExcludePath = (path) => {
  const parts = path.split('/');
  // Check if any folder in the path is an excluded folder
  for (const part of parts) {
    if (EXCLUDED_FOLDER_NAMES.includes(part)) {
      return true;
    }
  }
  return false;
};

// Helper to check if a file should be included (blacklist approach)
const shouldIncludeFile = (fileName, filePath) => {
  // Exclude system files
  if (SYSTEM_FILES.includes(fileName)) {
    return false;
  }

  // Check blocked extensions (installers/executables/compiled files)
  const ext = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
  if (BLOCKED_EXTENSIONS.includes(ext)) {
    return false;
  }

  // Allow all other file types
  return true;
};

// ============================================
// UPLOAD VIEW COMPONENT (Extracted to prevent re-creation on parent re-renders)
// ============================================
const UploadView = ({
  editingWorkflowId,
  setEditingWorkflowId,
  getValidAccessToken,
  setView,
  myDownloads,
  fetchMyDownloads,
  fetchWorkflows,
  fetchMyWorkflows,
  setSuccessBanner,
  subscriptionTier,
  setSourceFilter,
}) => {
  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    category: 'video_processing',
    tags: '',
    videoUrl: '',
    changelog: '',
    forkedFrom: '',
    price_cents: 0,
    visibility: 'public',
  });

  // Unified images state: array of { id, file?, url?, isNew }
  const [images, setImages] = useState([]);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [imageDragOver, setImageDragOver] = useState(false);

  const [workflowFiles, setWorkflowFiles] = useState([]);
  const [selectedWorkflowIndices, setSelectedWorkflowIndices] = useState(new Set());
  const [workflowFolderName, setWorkflowFolderName] = useState('');
  const [workflowFolderError, setWorkflowFolderError] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [originalWorkflow, setOriginalWorkflow] = useState(null); // Store full original workflow for edits

  // Drag-and-drop state
  const [isDragOver, setIsDragOver] = useState(false);
  const [processingDrop, setProcessingDrop] = useState(false);
  const [excludedFilesCount, setExcludedFilesCount] = useState(0);

  const isEditing = !!editingWorkflowId;

  // Fetch existing workflow data when editing
  useEffect(() => {
    const fetchExistingWorkflow = async () => {
      if (!editingWorkflowId) return;

      setLoadingExisting(true);
      try {
        const accessToken = await getValidAccessToken();

        const response = await fetch(`${API_BASE}/marketplace?id=${editingWorkflowId}`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch workflow');
        }

        const workflow = await response.json();

        // Store the original workflow for reference (to preserve workflow files)
        setOriginalWorkflow(workflow);

        // Pre-populate form fields
        setUploadData({
          title: workflow.title || '',
          description: workflow.description || '',
          category: workflow.category || 'video_processing',
          tags: workflow.tags ? workflow.tags.join(', ') : '',
          videoUrl: workflow.video_url || '',
          changelog: '',
          price_cents: workflow.price_cents || 0,
          visibility: workflow.visibility || 'public',
        });

        // Load existing images into unified array
        const loadedImages = [];

        if (workflow.thumbnail_url) {
          loadedImages.push({
            id: 'existing-thumbnail',
            file: null,
            url: workflow.thumbnail_url,
            isNew: false
          });
        }

        if (workflow.screenshot_urls && workflow.screenshot_urls.length > 0) {
          workflow.screenshot_urls.forEach((url, i) => {
            loadedImages.push({
              id: `existing-screenshot-${i}`,
              file: null,
              url,
              isNew: false
            });
          });
        }

        setImages(loadedImages);
        setMainImageIndex(0); // Thumbnail is always first when loading

      } catch (err) {
        console.error('Error fetching workflow:', err);
        setUploadError(`Failed to load workflow: ${err.message}`);
      } finally {
        setLoadingExisting(false);
      }
    };

    fetchExistingWorkflow();
  }, [editingWorkflowId, getValidAccessToken]);

  // Tag suggestions based on category
  const TAG_SUGGESTIONS = {
    video_processing: ['video', 'editing', 'converter', 'effects', 'ffmpeg'],
    image_processing: ['image', 'resize', 'filter', 'convert', 'batch'],
    data_tools: ['data', 'csv', 'analysis', 'chart', 'visualization'],
    file_utilities: ['file', 'pdf', 'converter', 'batch', 'tools'],
    automation: ['automation', 'script', 'scheduler', 'bot', 'workflow'],
    productivity: ['productivity', 'notes', 'organizer', 'tracker', 'planner'],
    creative_tools: ['creative', 'design', 'art', 'drawing', 'editor'],
    developer_tools: ['developer', 'code', 'api', 'database', 'testing'],
    games: ['game', 'gaming', 'simulation', 'entertainment', 'interactive'],
    gen_ai: ['ai', 'generative', 'chatbot', 'gpt', 'image-generation'],
    llm: ['llm', 'language-model', 'prompt', 'chat', 'completion'],
  };

  const suggestedTags = TAG_SUGGESTIONS[uploadData.category] || [];

  const addTag = (tag) => {
    const currentTags = uploadData.tags.split(',').map(t => t.trim()).filter(Boolean);
    if (!currentTags.includes(tag)) {
      const newTags = [...currentTags, tag].join(', ');
      setUploadData({ ...uploadData, tags: newTags });
    }
  };

  // Unified image handlers
  const handleImagesDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setImageDragOver(false);

    const files = Array.from(e.dataTransfer.files)
      .filter(f => f.type.startsWith('image/') && f.size <= 5 * 1024 * 1024);

    const remaining = 9 - images.length;
    if (remaining <= 0) return;

    const newImages = files.slice(0, remaining).map((file, i) => ({
      id: `new-${Date.now()}-${i}`,
      file,
      url: null,
      isNew: true
    }));

    setImages([...images, ...newImages]);
  };

  const handleImagesSelect = (e) => {
    const files = Array.from(e.target.files)
      .filter(f => f.size <= 5 * 1024 * 1024);

    const remaining = 9 - images.length;
    if (remaining <= 0) return;

    const newImages = files.slice(0, remaining).map((file, i) => ({
      id: `new-${Date.now()}-${i}`,
      file,
      url: null,
      isNew: true
    }));

    setImages([...images, ...newImages]);
    e.target.value = ''; // Reset input
  };

  const handleImageReorder = (dragIndex, dropIndex) => {
    if (dragIndex === dropIndex) return;

    const reordered = [...images];
    const [removed] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, removed);
    setImages(reordered);

    // Update main index if it was affected
    if (mainImageIndex === dragIndex) {
      setMainImageIndex(dropIndex);
    } else if (dragIndex < mainImageIndex && dropIndex >= mainImageIndex) {
      setMainImageIndex(mainImageIndex - 1);
    } else if (dragIndex > mainImageIndex && dropIndex <= mainImageIndex) {
      setMainImageIndex(mainImageIndex + 1);
    }
  };

  const handleSetMainImage = (index) => {
    setMainImageIndex(index);
  };

  const handleRemoveImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);

    // Adjust main index if needed
    if (newImages.length === 0) {
      setMainImageIndex(0);
    } else if (index === mainImageIndex) {
      setMainImageIndex(0);
    } else if (index < mainImageIndex) {
      setMainImageIndex(mainImageIndex - 1);
    }
  };

  // Validate workflow folder structure
  const validateWorkflowFolder = (files) => {
    if (files.length === 0) {
      return { valid: false, error: "No files selected" };
    }

    const paths = files.map(f => f.webkitRelativePath);
    const firstPath = paths[0];
    const pathParts = firstPath.split('/');

    // Get all unique root folders
    const uniqueRootFolders = new Set(paths.map(p => p.split('/')[0]));

    console.log('Validation check:', {
      totalFiles: files.length,
      uniqueRootFolders: Array.from(uniqueRootFolders),
      rootFolderCount: uniqueRootFolders.size,
      samplePaths: paths.slice(0, 5)
    });

    // If multiple root folders, they likely selected "workflows" folder itself
    if (uniqueRootFolders.size > 1) {
      return {
        valid: false,
        error: "Multiple workflow folders detected. It looks like you selected the 'workflows' parent folder. Please open the 'workflows' folder and select ONE specific workflow inside it."
      };
    }

    // Single root folder - good!
    const rootFolder = pathParts[0];

    // Check if folder structure seems too shallow (might be a subfolder)
    const minDepth = Math.min(...paths.map(p => p.split('/').length));
    const maxDepth = Math.max(...paths.map(p => p.split('/').length));

    // If max depth is only 1, they selected files in the root with no structure
    if (maxDepth === 1) {
      return {
        valid: false,
        error: "No folder structure detected. Please select a workflow folder, not individual files."
      };
    }

    return { valid: true, folderName: rootFolder };
  };

  // Build tree structure from flat file array
  const buildFileTree = (files) => {
    const root = {
      name: 'root',
      type: 'folder',
      children: {},
      files: [],
      path: '',
    };

    files.forEach((fileObj, index) => {
      const parts = fileObj.relativePath.split('/');
      let currentLevel = root;

      // Navigate/create folder structure
      for (let i = 0; i < parts.length - 1; i++) {
        const folderName = parts[i];
        if (!currentLevel.children[folderName]) {
          currentLevel.children[folderName] = {
            name: folderName,
            type: 'folder',
            children: {},
            files: [],
            path: parts.slice(0, i + 1).join('/'),
          };
        }
        currentLevel = currentLevel.children[folderName];
      }

      // Add file to current folder
      currentLevel.files.push({
        ...fileObj,
        index,
        name: parts[parts.length - 1],
      });
    });

    return root;
  };

  // ============================================
  // DRAG AND DROP HANDLERS
  // ============================================

  // Recursively read directory entries from drag-and-drop
  const readDirectoryEntries = async (directoryEntry, basePath = '') => {
    const entries = [];
    const reader = directoryEntry.createReader();

    // readEntries may not return all entries at once, need to call until empty
    const readBatch = () => {
      return new Promise((resolve, reject) => {
        reader.readEntries(resolve, reject);
      });
    };

    let batch;
    do {
      batch = await readBatch();
      entries.push(...batch);
    } while (batch.length > 0);

    return entries;
  };

  // Read a file entry and return the File object
  const readFileEntry = (fileEntry) => {
    return new Promise((resolve, reject) => {
      fileEntry.file(resolve, reject);
    });
  };

  // Process dropped folder - recursively read and filter files
  const processDroppedFolder = async (dataTransfer) => {
    const items = dataTransfer.items;
    const filesWithPaths = [];
    let excludedCount = 0;
    let rootFolderName = '';

    // Get entries from dropped items
    const entries = [];
    for (let i = 0; i < items.length; i++) {
      const entry = items[i].webkitGetAsEntry();
      if (entry) {
        entries.push(entry);
      }
    }

    // Recursive function to traverse directory
    const traverseDirectory = async (entry, currentPath) => {
      if (entry.isFile) {
        const fileName = entry.name;
        const filePath = currentPath ? `${currentPath}/${fileName}` : fileName;

        // Check if path is excluded
        if (shouldExcludePath(filePath)) {
          excludedCount++;
          return;
        }

        // Check if file should be included
        if (shouldIncludeFile(fileName, filePath)) {
          try {
            const file = await readFileEntry(entry);
            filesWithPaths.push({
              file,
              relativePath: filePath
            });
          } catch (err) {
            console.warn(`Could not read file: ${filePath}`, err);
          }
        } else {
          excludedCount++;
        }
      } else if (entry.isDirectory) {
        const folderName = entry.name;
        const folderPath = currentPath ? `${currentPath}/${folderName}` : folderName;

        // Skip excluded folders entirely (this is the key optimization!)
        if (EXCLUDED_FOLDER_NAMES.includes(folderName)) {
          console.log(`Skipping excluded folder: ${folderPath}`);
          // Don't count individual files, just note the folder was skipped
          excludedCount++;
          return;
        }

        // Read directory contents
        try {
          const childEntries = await readDirectoryEntries(entry);
          for (const childEntry of childEntries) {
            await traverseDirectory(childEntry, folderPath);
          }
        } catch (err) {
          console.warn(`Could not read directory: ${folderPath}`, err);
        }
      }
    };

    // Determine drop mode and process accordingly
    if (entries.length === 1 && entries[0].isDirectory) {
      // SINGLE FOLDER MODE - use folder name, auto-filter venv
      rootFolderName = entries[0].name;

      try {
        const childEntries = await readDirectoryEntries(entries[0]);
        for (const childEntry of childEntries) {
          await traverseDirectory(childEntry, '');
        }
      } catch (err) {
        console.error('Error reading dropped folder:', err);
        setWorkflowFolderError("Could not read the dropped folder. Please try again.");
        return null;
      }
    } else if (entries.length >= 1) {
      // MULTI-ITEM MODE - user selected specific items (excluding venv themselves)
      // rootFolderName will be empty - we'll use the Title field on submit
      rootFolderName = '';

      console.log(`Multi-item drop: ${entries.length} items`);

      for (const entry of entries) {
        if (entry.isDirectory) {
          // Still filter venv if they accidentally included it
          if (EXCLUDED_FOLDER_NAMES.includes(entry.name)) {
            console.log(`Skipping excluded folder in multi-drop: ${entry.name}`);
            excludedCount++;
            continue;
          }
          // Traverse this folder, using its name as the base path
          try {
            const childEntries = await readDirectoryEntries(entry);
            for (const childEntry of childEntries) {
              await traverseDirectory(childEntry, entry.name);
            }
          } catch (err) {
            console.warn(`Could not read directory: ${entry.name}`, err);
          }
        } else if (entry.isFile) {
          // Process individual file at root level
          if (shouldIncludeFile(entry.name, entry.name)) {
            try {
              const file = await readFileEntry(entry);
              filesWithPaths.push({ file, relativePath: entry.name });
            } catch (err) {
              console.warn(`Could not read file: ${entry.name}`, err);
            }
          } else {
            excludedCount++;
          }
        }
      }
    }

    return { filesWithPaths, excludedCount, rootFolderName };
  };

  // Handle files from drop or file input
  const processAndSetFiles = (filesWithPaths, excludedCount, folderName) => {
    if (filesWithPaths.length === 0) {
      setWorkflowFolderError("No valid workflow files found. Allowed: .py, .ipynb, .json, .txt, .md, .yaml, .yml, .toml, .cfg, .ini, .csv, .xml");
      return;
    }

    // Calculate total size
    const totalSize = filesWithPaths.reduce((sum, f) => sum + f.file.size, 0);
    const maxSize = 100 * 1024 * 1024; // 100MB

    if (totalSize > maxSize) {
      setWorkflowFolderError(`Workflow folder is too large (${(totalSize / 1024 / 1024).toFixed(1)}MB). Maximum size is 100MB.`);
      return;
    }

    setWorkflowFiles(filesWithPaths);
    setWorkflowFolderName(folderName);
    setExcludedFilesCount(excludedCount);

    // Initialize all files as selected by default
    setSelectedWorkflowIndices(new Set(filesWithPaths.map((_, index) => index)));

    // Initialize all folders as expanded
    const allFolderPaths = new Set();
    filesWithPaths.forEach(fileObj => {
      const parts = fileObj.relativePath.split('/');
      for (let i = 1; i < parts.length; i++) {
        allFolderPaths.add(parts.slice(0, i).join('/'));
      }
    });
    setExpandedFolders(allFolderPaths);

    console.log(`Selected ${filesWithPaths.length} workflow files (${(totalSize / 1024 / 1024).toFixed(2)}MB), excluded ${excludedCount} items`);
  };

  // Drag event handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setWorkflowFolderError(null);
    setProcessingDrop(true);

    try {
      const result = await processDroppedFolder(e.dataTransfer);
      if (result) {
        processAndSetFiles(result.filesWithPaths, result.excludedCount, result.rootFolderName);
      }
    } catch (err) {
      console.error('Drop processing error:', err);
      setWorkflowFolderError("Error processing dropped folder. Please try again.");
    } finally {
      setProcessingDrop(false);
    }
  };

  // Handle individual file selection (for private uploads)
  const handleIndividualFilesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setWorkflowFolderError(null);

    const filesWithPaths = files.map(file => ({
      file,
      relativePath: file.name
    }));

    processAndSetFiles(filesWithPaths, 0, null);
  };

  const handleWorkflowFolderChange = (e) => {
    const files = Array.from(e.target.files);

    if (files.length === 0) return;

    // Clear any previous errors
    setWorkflowFolderError(null);

    // Validate folder structure
    const validation = validateWorkflowFolder(files);

    if (!validation.valid) {
      if (validation.needsConfirmation) {
        const proceed = window.confirm(validation.warning);
        if (!proceed) {
          e.target.value = ''; // Reset file input
          setWorkflowFolderError(validation.warning);
          return;
        }
      } else {
        setWorkflowFolderError(validation.error);
        e.target.value = ''; // Reset file input
        return;
      }
    }

    // Get folder name from first file's path
    const firstPath = files[0].webkitRelativePath;
    const folderName = firstPath.split('/')[0];

    // Filter files using shared constants
    let excludedCount = 0;
    const filteredFiles = files.filter(file => {
      const path = file.webkitRelativePath;

      // Check if path contains excluded folders
      if (shouldExcludePath(path)) {
        excludedCount++;
        return false;
      }

      // Check if file should be included
      const fileName = path.split('/').pop();
      if (!shouldIncludeFile(fileName, path)) {
        excludedCount++;
        return false;
      }

      return true;
    });

    // Store files with their relative paths (remove top-level folder name)
    const filesWithPaths = filteredFiles.map(file => ({
      file,
      relativePath: file.webkitRelativePath.split('/').slice(1).join('/')
    }));

    // Use shared function to set files and state
    processAndSetFiles(filesWithPaths, excludedCount, folderName);
  };

  // Toggle individual file selection
  const toggleFileSelection = (index) => {
    const newSelection = new Set(selectedWorkflowIndices);
    if (newSelection.has(index)) {
      newSelection.delete(index);
    } else {
      newSelection.add(index);
    }
    setSelectedWorkflowIndices(newSelection);
  };

  // Toggle folder selection (all files in folder)
  const toggleFolderSelection = (indices, select) => {
    const newSelection = new Set(selectedWorkflowIndices);
    indices.forEach(index => {
      if (select) {
        newSelection.add(index);
      } else {
        newSelection.delete(index);
      }
    });
    setSelectedWorkflowIndices(newSelection);
  };

  // Select all workflow files
  const selectAllFiles = () => {
    setSelectedWorkflowIndices(new Set(workflowFiles.map((_, index) => index)));
  };

  // Deselect all workflow files
  const deselectAllFiles = () => {
    setSelectedWorkflowIndices(new Set());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    setUploadError(null);

    try {
      // Validate required fields
      if (!uploadData.title || !uploadData.description) {
        throw new Error('Title and description are required');
      }

      // At least one image is required
      if (images.length === 0) {
        throw new Error('At least one image is required');
      }

      // Validate that at least one workflow file is selected if any are present
      if (workflowFiles.length > 0 && selectedWorkflowIndices.size === 0) {
        throw new Error('Please select at least one workflow file to upload');
      }

      // Get access token
      const accessToken = await getValidAccessToken();

      // Determine listing ID
      const listingId = isEditing ? editingWorkflowId : null;
      let finalListingId = listingId; // Initialize early for metadata-only updates

      // Step 1: Prepare file list for presigned URL generation (only if files are being uploaded)
      const files = [];

      // Add workflow files if provided (only selected ones)
      workflowFiles.forEach((fileObj, index) => {
        if (selectedWorkflowIndices.has(index)) {
          files.push({
            id: `workflow_${index}`,
            name: fileObj.file.name,
            size: fileObj.file.size,
            type: 'workflow',
            contentType: fileObj.file.type || 'application/octet-stream',
            path: fileObj.relativePath // Preserves folder structure
          });
        }
      });

      // Add images (main thumbnail and screenshots) - only new uploads
      const mainImage = images[mainImageIndex];
      const screenshotImages = images.filter((_, i) => i !== mainImageIndex);

      // Add main thumbnail if it's a new upload
      if (mainImage && mainImage.isNew && mainImage.file) {
        files.push({
          id: 'thumbnail',
          name: mainImage.file.name,
          size: mainImage.file.size,
          type: 'image',
          contentType: mainImage.file.type,
          path: mainImage.file.name
        });
      }

      // Add screenshot images if they're new uploads
      screenshotImages.forEach((img, index) => {
        if (img.isNew && img.file) {
          files.push({
            id: `screenshot_${index}`,
            name: img.file.name,
            size: img.file.size,
            type: 'image',
            contentType: img.file.type,
            path: img.file.name
          });
        }
      });

      // Step 2: Initialize upload - get presigned URLs from backend (only if there are files to upload)
      let uploadUrls = [];

      if (files.length > 0) {
        console.log('Requesting presigned URLs for', files.length, 'files...');
        console.log('Files to upload:', files);

        const initBody = {
          files,
          metadata: {
            title: uploadData.title,
            description: uploadData.description,
            category: uploadData.category
          }
        };

        if (isEditing) {
          initBody.listingId = editingWorkflowId;
        }

        const initResponse = await fetch(`${API_BASE}/marketplace-upload-init`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          body: JSON.stringify(initBody)
        });

        if (!initResponse.ok) {
          const errorData = await initResponse.json();
          console.error('Upload init failed:', errorData);
          // Check for payment method required error
          if (initResponse.status === 403 && errorData.code === 'payment_method_required') {
            setUploadError('payment_method_required');
            setUploading(false);
            return;
          }
          throw new Error(errorData.error || 'Failed to initialize upload');
        }

        const initData = await initResponse.json();
        uploadUrls = initData.uploadUrls || [];
        finalListingId = initData.listingId;
        console.log('Got presigned URLs for listing:', finalListingId);
      } else if (!isEditing) {
        throw new Error('No files to upload');
      } else {
        // Edit mode with no new files - metadata-only update
        console.log('Metadata-only update for listing:', finalListingId);
      }

      // Step 3: Upload files to S3 using presigned URLs
      const uploadedFiles = {};

      for (const uploadUrl of uploadUrls) {
        let fileToUpload;

        if (uploadUrl.fileId === 'thumbnail') {
          // Main image (thumbnail)
          fileToUpload = mainImage?.file;
        } else if (uploadUrl.fileId.startsWith('screenshot_')) {
          const screenshotIndex = parseInt(uploadUrl.fileId.split('_')[1]);
          fileToUpload = screenshotImages[screenshotIndex]?.file;
        } else if (uploadUrl.fileId.startsWith('workflow_')) {
          const workflowIndex = parseInt(uploadUrl.fileId.split('_')[1]);
          fileToUpload = workflowFiles[workflowIndex].file;
        }

        if (!fileToUpload) {
          console.warn('No file found for upload URL:', uploadUrl.fileId);
          continue;
        }

        console.log(`Uploading ${uploadUrl.fileId} to S3...`);

        const s3Response = await fetch(uploadUrl.uploadUrl, {
          method: 'PUT',
          body: fileToUpload,
          headers: {
            'Content-Type': fileToUpload.type || 'application/octet-stream'
          }
        });

        if (!s3Response.ok) {
          throw new Error(`Failed to upload ${uploadUrl.fileName} to S3`);
        }

        // Track uploaded file URLs (construct full S3 URL from key)
        uploadedFiles[uploadUrl.fileId] = uploadUrl.s3Key;
        console.log(`✓ Uploaded ${uploadUrl.fileId}`);
      }

      // Step 4: Complete upload - create database listing
      console.log('Completing upload...');

      // Construct full S3 URLs for database
      const bucketRegion = 'ap-southeast-2'; // From .env
      const bucketName = 'contextui-exchange';

      // Build thumbnail URL: use new upload if available, otherwise existing URL
      const thumbnailUrl = uploadedFiles.thumbnail
        ? `https://${bucketName}.s3.${bucketRegion}.amazonaws.com/${uploadedFiles.thumbnail}`
        : (mainImage && !mainImage.isNew ? mainImage.url : null);

      // Build screenshot URLs: combine new uploads with existing URLs (preserving order)
      const screenshotUrls = screenshotImages.map((img, index) => {
        if (img.isNew && uploadedFiles[`screenshot_${index}`]) {
          return `https://${bucketName}.s3.${bucketRegion}.amazonaws.com/${uploadedFiles[`screenshot_${index}`]}`;
        }
        return img.url; // Existing URL
      }).filter(Boolean);

      // Build workflow files list for database (only selected files)
      const workflowFilesList = workflowFiles
        .filter((_, index) => selectedWorkflowIndices.has(index))
        .map((fileObj) => ({
          path: fileObj.relativePath,
          size: fileObj.file.size,
          name: fileObj.file.name
        }));

      const completeResponse = await fetch(`${API_BASE}/marketplace-upload-complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          listingId: finalListingId,
          title: uploadData.title,
          description: uploadData.description,
          category: uploadData.category,
          tags: uploadData.tags,
          thumbnailUrl: thumbnailUrl,
          screenshotUrls: screenshotUrls,
          videoUrl: uploadData.videoUrl || null,
          contextUiVersionMin: '0.1.0',
          fileSizeBytes: files.reduce((sum, f) => sum + f.size, 0),
          workflowFiles: workflowFilesList.length > 0 ? workflowFilesList : undefined,
          preserveExistingWorkflowFiles: isEditing && workflowFilesList.length === 0,
          existingWorkflowFiles: (isEditing && workflowFilesList.length === 0)
            ? originalWorkflow?.workflow_files
            : undefined,
          changelog: uploadData.changelog || (isEditing ? 'Updated workflow' : 'Initial release'),
          forkedFrom: uploadData.forkedFrom || null,
          isUpdate: isEditing,
          priceCents: uploadData.price_cents || 0,
          visibility: uploadData.visibility || 'public'
        })
      });

      if (!completeResponse.ok) {
        const errorData = await completeResponse.json();
        if (completeResponse.status === 403 && errorData.code === 'payment_method_required') {
          // No payment method on file - show modal
          setUploadError('payment_method_required');
          return;
        }
        if (completeResponse.status === 403 && errorData.code === 'pro_required') {
          setUploadError(errorData.message || 'Pro subscription required for private workflows.');
          return;
        }
        if (completeResponse.status === 403 && errorData.code === 'storage_limit') {
          setUploadError(errorData.message || 'Private storage limit exceeded.');
          return;
        }
        throw new Error(errorData.error || 'Failed to complete upload');
      }

      const { listing, requiresReapproval } = await completeResponse.json();
      console.log(isEditing ? 'Update complete!' : 'Upload complete! Listing created:', listing.id);

      // Success - redirect based on visibility
      setEditingWorkflowId(null);

      if (uploadData.visibility === 'private') {
        // Private workflows go to the browse view with private tab selected
        setSourceFilter('private');
        setView('browse');
        fetchWorkflows();
        setSuccessBanner({
          type: isEditing ? 'updated' : 'published_private',
          workflowTitle: uploadData.title
        });
      } else {
        // Public workflows go to my-workflows view
        setView('my-workflows');
        fetchMyWorkflows();

        if (isEditing) {
          setSuccessBanner({
            type: requiresReapproval ? 'resubmitted' : 'updated',
            workflowTitle: uploadData.title
          });
          if (detailWorkflow && detailWorkflow.id === editingWorkflowId) {
            setDetailWorkflow(null);
          }
        } else {
          setSuccessBanner({ type: 'submitted', workflowTitle: uploadData.title });
        }
      }

    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pt-20 px-4 py-12">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => {
            setEditingWorkflowId(null);
            setView(isEditing ? 'my-workflows' : 'browse');
          }}
          className="mb-6 flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          {isEditing ? 'Back to Published' : 'Back to Browse'}
        </button>

        <h1 className="text-3xl font-bold mb-2">{isEditing ? 'Update Workflow' : 'Upload Workflow'}</h1>
        <p className="text-gray-400 mb-8">
          {isEditing ? 'Update your workflow details and files' : 'Share your Python workflow with the community'}
        </p>

        {loadingExisting ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
              <p className="mt-4 text-gray-400">Loading workflow...</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
          {/* Existing Workflow Files (when editing) */}
          {isEditing && originalWorkflow?.workflow_files?.length > 0 && workflowFiles.length === 0 && (
            <div className="mb-4 p-4 bg-gray-900 border border-gray-800 rounded-lg">
              <h3 className="text-sm font-medium mb-2 text-gray-400">Current Workflow Files</h3>
              <div className="space-y-1">
                {originalWorkflow.workflow_files.map((file, i) => (
                  <div key={i} className="text-sm text-gray-500 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    {file.path || file.name || `file-${i}`}
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-600 mt-2">
                These files will be preserved unless you upload new ones below.
              </p>
            </div>
          )}

          {/* Workflow Folder Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">
              1. Workflow Folder <span className="text-gray-500">(optional - can upload later)</span>
            </label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer bg-gray-900/50 ${
                isDragOver
                  ? 'border-blue-500 bg-blue-500/10 scale-[1.02]'
                  : 'border-gray-700 hover:border-blue-500'
              } ${processingDrop ? 'opacity-60 cursor-wait' : ''}`}
            >
              {/* Folder picker (always available) */}
              <label className="cursor-pointer block">
                <input
                  type="file"
                  webkitdirectory="true"
                  directory="true"
                  multiple
                  onChange={handleWorkflowFolderChange}
                  className="hidden"
                  disabled={processingDrop}
                />
                {processingDrop ? (
                  <div>
                    <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent mx-auto mb-3"></div>
                    <p className="text-blue-400 mb-2">Processing files...</p>
                    <p className="text-sm text-gray-500">Filtering out venv, cache, system files, and executables</p>
                  </div>
                ) : workflowFiles.length > 0 ? (
                  <div>
                    <Code className="h-12 w-12 text-green-500 mx-auto mb-3" />
                    <p className="text-green-400 mb-2">
                      ✓ {workflowFolderName || 'Selected files'} ({selectedWorkflowIndices.size} of {workflowFiles.length} files selected)
                    </p>
                    <p className="text-sm text-gray-500">
                      {(workflowFiles.filter((_, i) => selectedWorkflowIndices.has(i)).reduce((sum, f) => sum + f.file.size, 0) / 1024 / 1024).toFixed(2)}MB total
                    </p>
                    {excludedFilesCount > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        {excludedFilesCount} items excluded (venv, cache, system files, executables)
                      </p>
                    )}
                    {!workflowFolderName && (
                      <p className="text-xs text-yellow-500 mt-1">
                        Folder name will be derived from Title field
                      </p>
                    )}
                    <p className="text-xs text-gray-600 mt-2">Drop another folder or click to change</p>
                  </div>
                ) : isDragOver ? (
                  <div>
                    <Upload className="h-12 w-12 text-blue-500 mx-auto mb-3 animate-bounce" />
                    <p className="text-blue-400 mb-2 font-medium">Drop your workflow files here</p>
                    <p className="text-sm text-gray-500">Release to upload</p>
                  </div>
                ) : (
                  <div>
                    <Upload className="h-12 w-12 text-gray-500 mx-auto mb-3" />
                    <p className="text-gray-400 mb-2">Drag & drop your workflow folder here</p>
                    <p className="text-sm text-gray-500">or click to select a folder</p>
                    <p className="text-xs text-gray-600 mt-2">Max 100MB • Excludes venv, __pycache__, .git automatically</p>
                  </div>
                )}
              </label>
              {/* Individual file picker for private uploads */}
              {uploadData.visibility === 'private' && !processingDrop && workflowFiles.length === 0 && (
                <div className="mt-3 border-t border-gray-700 pt-3">
                  <label className="cursor-pointer inline-flex items-center gap-2 text-sm text-indigo-400 hover:text-indigo-300 transition-colors">
                    <input
                      type="file"
                      multiple
                      onChange={handleIndividualFilesChange}
                      className="hidden"
                    />
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Or select individual files instead
                  </label>
                </div>
              )}
            </div>
            {workflowFiles.length > 0 && (
              <div className="mt-3 p-3 bg-gray-900 rounded border border-gray-800 text-sm">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-gray-400 font-medium">Files to upload:</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={selectAllFiles}
                      className="text-xs px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={deselectAllFiles}
                      className="text-xs px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded transition-colors"
                    >
                      Deselect All
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mb-3">
                  {(workflowFiles.filter((_, i) => selectedWorkflowIndices.has(i)).reduce((sum, f) => sum + f.file.size, 0) / 1024 / 1024).toFixed(2)}MB total
                </p>
                <div className="max-h-96 overflow-y-auto border border-gray-700 rounded-lg p-2 bg-gray-900/50">
                  <FileTreeNode
                    node={buildFileTree(workflowFiles)}
                    selectedIndices={selectedWorkflowIndices}
                    onToggleFile={toggleFileSelection}
                    onToggleFolder={toggleFolderSelection}
                    expandedFolders={expandedFolders}
                    setExpandedFolders={setExpandedFolders}
                    depth={0}
                  />
                </div>
              </div>
            )}

            {/* Error Display */}
            {workflowFolderError && (
              <div className="mt-3 p-3 bg-red-900/20 border border-red-700/50 rounded text-sm">
                <p className="text-red-300">❌ {workflowFolderError}</p>
              </div>
            )}
          </div>

          {/* Images - Unified drag & drop */}
          <div>
            <label className="block text-sm font-medium mb-2">
              2. Images <span className="text-red-500">*</span>
            </label>
            <p className="text-sm text-gray-500 mb-3">
              Upload up to 9 images. Drag to reorder. Click star to set main thumbnail.
            </p>

            {/* Drag-drop zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setImageDragOver(true); }}
              onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setImageDragOver(false); }}
              onDrop={handleImagesDrop}
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors
                ${imageDragOver ? 'border-blue-500 bg-blue-500/10' : 'border-gray-700 hover:border-blue-500'}
                ${images.length >= 9 ? 'opacity-50' : 'cursor-pointer'}`}
            >
              <label className={`cursor-pointer block ${images.length >= 9 ? 'pointer-events-none' : ''}`}>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImagesSelect}
                  className="hidden"
                  disabled={images.length >= 9}
                />
                <ImageIcon className="h-10 w-10 text-gray-500 mx-auto mb-2" />
                <p className="text-gray-400">Drag & drop images here</p>
                <p className="text-sm text-gray-500">or click to select ({9 - images.length} remaining)</p>
                <p className="text-xs text-gray-600 mt-1">Max 5MB per image • Recommended 900x1200px (3:4 portrait)</p>
              </label>
            </div>

            {/* Image grid with reordering */}
            {images.length > 0 && (
              <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                {images.map((img, index) => (
                  <div
                    key={img.id}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('imageIndex', index.toString());
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const dragIndex = parseInt(e.dataTransfer.getData('imageIndex'));
                      if (!isNaN(dragIndex)) {
                        handleImageReorder(dragIndex, index);
                      }
                    }}
                    className={`relative group rounded-lg overflow-hidden cursor-move border-2 transition-all
                      ${index === mainImageIndex ? 'border-yellow-500 ring-2 ring-yellow-500/30' : 'border-transparent hover:border-gray-600'}`}
                  >
                    <img
                      src={img.file ? URL.createObjectURL(img.file) : img.url}
                      alt={`Image ${index + 1}`}
                      className="w-full h-32 object-cover"
                    />

                    {/* Main thumbnail badge */}
                    {index === mainImageIndex && (
                      <div className="absolute top-2 left-2 bg-yellow-500 text-black px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1">
                        <Star className="h-3 w-3 fill-current" />
                        Main
                      </div>
                    )}

                    {/* Drag handle indicator */}
                    <div className="absolute bottom-2 left-2 text-white/60 bg-black/40 rounded px-1">
                      <GripVertical className="h-4 w-4" />
                    </div>

                    {/* Overlay with actions */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      {/* Set as main button */}
                      {index !== mainImageIndex && (
                        <button
                          type="button"
                          onClick={() => handleSetMainImage(index)}
                          className="p-2 bg-yellow-500 rounded-full hover:bg-yellow-400 transition-colors"
                          title="Set as main thumbnail"
                        >
                          <Star className="h-4 w-4 text-black" />
                        </button>
                      )}

                      {/* Remove button */}
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="p-2 bg-red-500 rounded-full hover:bg-red-400 transition-colors"
                        title="Remove image"
                      >
                        <X className="h-4 w-4 text-white" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Required warning */}
            {images.length === 0 && (
              <p className="mt-2 text-sm text-red-400">At least one image is required</p>
            )}
          </div>

          {/* Video URL */}
          <div>
            <label className="block text-sm font-medium mb-2">
              3. Demo Video (optional)
            </label>
            <input
              type="url"
              value={uploadData.videoUrl}
              onChange={(e) => setUploadData({ ...uploadData, videoUrl: e.target.value })}
              placeholder="https://youtube.com/watch?v=..."
              className="w-full px-4 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-sm text-gray-500 mt-1">YouTube or Vimeo URL</p>
          </div>

          {/* Basic Info */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={uploadData.title}
                onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                placeholder="Background Remover V2"
                className="w-full px-4 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={uploadData.category}
                onChange={(e) => setUploadData({ ...uploadData, category: e.target.value })}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="video_processing">Video Processing</option>
                <option value="image_processing">Image Processing</option>
                <option value="data_tools">Data Tools</option>
                <option value="file_utilities">File Utilities</option>
                <option value="games">Games</option>
                <option value="automation">Automation</option>
                <option value="productivity">Productivity</option>
                <option value="creative_tools">Creative Tools</option>
                <option value="developer_tools">Developer Tools</option>
                <option value="gen_ai">AI / Gen AI</option>
                <option value="llm">LLM Tools</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={uploadData.description}
              onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
              placeholder="Describe what your workflow does..."
              rows={6}
              className="w-full px-4 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium mb-2">Tags</label>
            <input
              type="text"
              value={uploadData.tags}
              onChange={(e) => setUploadData({ ...uploadData, tags: e.target.value })}
              placeholder="video, background, converter (comma separated)"
              className="w-full px-4 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {suggestedTags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs text-gray-500 self-center">Suggested:</span>
                {suggestedTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => addTag(tag)}
                    className="px-2 py-1 bg-gray-800 hover:bg-gray-700 text-white text-xs rounded transition-colors"
                  >
                    + {tag}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Visibility */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Visibility
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="visibility"
                  value="public"
                  checked={uploadData.visibility === 'public'}
                  onChange={() => setUploadData({ ...uploadData, visibility: 'public' })}
                  className="text-blue-600"
                />
                <span className="text-gray-300">Public</span>
              </label>
              <label className={`flex items-center gap-2 ${subscriptionTier === 'pro' ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                <input
                  type="radio"
                  name="visibility"
                  value="private"
                  checked={uploadData.visibility === 'private'}
                  onChange={() => subscriptionTier === 'pro' && setUploadData({ ...uploadData, visibility: 'private', price_cents: 0 })}
                  disabled={subscriptionTier !== 'pro'}
                  className="text-blue-600"
                />
                <span className="text-gray-300">Private</span>
                {subscriptionTier !== 'pro' && (
                  <span className="text-xs bg-indigo-600/30 text-indigo-300 px-2 py-0.5 rounded">Pro</span>
                )}
              </label>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {uploadData.visibility === 'private'
                ? 'Only you can see this workflow. Skips admin review.'
                : 'Visible to everyone after admin review.'}
            </p>
          </div>

          {/* Download Price - hidden for private workflows */}
          {uploadData.visibility !== 'private' && (
          <div>
            <label className="block text-sm font-medium mb-2">
              Download Price <span className="text-gray-500">(optional)</span>
            </label>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min="0"
                step="1"
                value={uploadData.price_cents === 0 ? '' : uploadData.price_cents}
                onChange={(e) => setUploadData({ ...uploadData, price_cents: e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value) || 0) })}
                placeholder="0"
                className="w-32 px-4 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-gray-400">cents</span>
              {uploadData.price_cents > 0 && (
                <span className="text-gray-500 text-sm">
                  (${(uploadData.price_cents / 100).toFixed(2)} USD)
                </span>
              )}
            </div>
            {uploadData.price_cents > 0 ? (
              <p className="text-sm text-green-400 mt-1">
                💰 You earn <strong>${(Math.floor(uploadData.price_cents * 0.70) / 100).toFixed(2)}</strong> per sale (70% creator share)
              </p>
            ) : (
              <p className="text-sm text-gray-500 mt-1">
                Set to 0 for free downloads. Enter amount in cents (e.g., 100 = $1.00)
              </p>
            )}
          </div>
          )}

          {/* Fork Attribution */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Forked from another workflow? <span className="text-gray-500">(optional)</span>
            </label>

            <div className="mb-3">
              <button
                type="button"
                onClick={() => {
                  if (myDownloads.length === 0) {
                    fetchMyDownloads();
                  }
                  document.getElementById('fork-dropdown').classList.toggle('hidden');
                }}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Choose from my downloads ({myDownloads.length})
              </button>

              {/* Dropdown */}
              <div id="fork-dropdown" className="hidden mt-2 max-h-60 overflow-y-auto bg-gray-800 border border-gray-700 rounded-lg">
                {myDownloads.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No downloads yet
                  </div>
                ) : (
                  <div className="p-2 space-y-1">
                    {myDownloads.map((download) => (
                      <button
                        key={download.id}
                        type="button"
                        onClick={() => {
                          setUploadData({ ...uploadData, forkedFrom: download.id });
                          document.getElementById('fork-dropdown').classList.add('hidden');
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-700 rounded text-sm transition-colors"
                      >
                        <div className="font-medium text-white">{download.title}</div>
                        <div className="text-xs text-gray-400">by {download.creator}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <input
              type="text"
              value={uploadData.forkedFrom || ''}
              onChange={(e) => setUploadData({ ...uploadData, forkedFrom: e.target.value })}
              placeholder="Paste workflow ID or URL"
              className="w-full px-4 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-sm text-gray-500 mt-1">
              If this workflow is an extension of previous work, give credit to the original workflow creator
            </p>
          </div>

          {/* Changelog - Show when editing */}
          {isEditing && (
            <div>
              <label className="block text-sm font-medium mb-2">
                What's New in This Version? <span className="text-gray-500">(optional)</span>
              </label>
              <textarea
                value={uploadData.changelog}
                onChange={(e) => setUploadData({ ...uploadData, changelog: e.target.value })}
                placeholder="Describe what you changed in this update..."
                rows={3}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-sm text-gray-500 mt-1">
                Help users understand what's new or improved
              </p>
            </div>
          )}

          {/* Error Message */}
          {uploadError && uploadError !== 'payment_method_required' && (
            <div className="p-4 bg-red-900/50 border border-red-700 rounded-lg text-red-200">
              {uploadError}
            </div>
          )}

          {/* Verification Notice */}
          <div className="p-4 bg-blue-900/30 border border-blue-700/50 rounded-lg">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-blue-200 text-sm font-medium">Verified publishers only</p>
                <p className="text-blue-300/70 text-sm mt-1">
                  A verified payment method is required to publish on the Exchange. This helps us maintain a safe and trustworthy marketplace for everyone.
                </p>
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-4 pt-6">
            <button
              type="button"
              onClick={() => {
                setEditingWorkflowId(null);
                setView(isEditing ? 'my-workflows' : 'browse');
              }}
              disabled={uploading}
              className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <>
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-5 w-5" />
                  {isEditing ? 'Update Workflow' : 'Publish to Exchange'}
                </>
              )}
            </button>
          </div>
        </form>
        )}

        {/* Add Payment Method Modal for Upload */}
        <AddPaymentMethodModal
          isOpen={uploadError === 'payment_method_required'}
          onClose={() => setUploadError(null)}
          onSuccess={() => {
            setUploadError(null);
            // User can retry the upload now
          }}
          context="upload"
        />
      </div>
    </div>
  );
};

const Exchange = () => {
  const { user, isAuthenticated, getValidAccessToken } = useWorkOS();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if running in embedded mode (inside Electron app)
  const isEmbedded = new URLSearchParams(location.search).get('embedded') === 'true';

  const [view, setView] = useState('browse'); // browse, my-workflows, my-downloads, detail, upload, review-queue, deleted-workflows, comments-log
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('updated');
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('official'); // 'official' | 'community'
  const [editingWorkflowId, setEditingWorkflowId] = useState(null);

  // API State
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 0, total: 0 });

  // My Workflows State
  const [myWorkflows, setMyWorkflows] = useState([]);
  const [myWorkflowsLoading, setMyWorkflowsLoading] = useState(false);
  const [myWorkflowsError, setMyWorkflowsError] = useState(null);

  // My Downloads State
  const [myDownloads, setMyDownloads] = useState([]);
  const [myDownloadsLoading, setMyDownloadsLoading] = useState(false);
  const [myDownloadsError, setMyDownloadsError] = useState(null);

  // Subscription State
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState('free');
  const [checkingSubscription, setCheckingSubscription] = useState(false);

  // Likes State - Track which workflows the current user has liked
  const [likedWorkflows, setLikedWorkflows] = useState(new Set());

  // Admin Review Queue State
  const [reviewQueue, setReviewQueue] = useState([]);
  const [reviewQueueLoading, setReviewQueueLoading] = useState(false);
  const [reviewQueueError, setReviewQueueError] = useState(null);
  const [reviewQueueSearch, setReviewQueueSearch] = useState('');
  const [rejectingWorkflow, setRejectingWorkflow] = useState(null); // For rejection modal
  const [viewingFeedbackWorkflow, setViewingFeedbackWorkflow] = useState(null); // For admin feedback panel
  const [adminFeedbackAccessToken, setAdminFeedbackAccessToken] = useState(null);

  // Admin Deleted Workflows State
  const [deletedWorkflows, setDeletedWorkflows] = useState([]);
  const [deletedWorkflowsLoading, setDeletedWorkflowsLoading] = useState(false);
  const [deletedWorkflowsError, setDeletedWorkflowsError] = useState(null);
  const [hardDeleting, setHardDeleting] = useState(null); // Track which workflow is being hard deleted

  // Success Banner State (for showing submission feedback)
  const [successBanner, setSuccessBanner] = useState(null); // { type: 'submitted' | 'updated' | 'resubmitted' | 'published_private', workflowTitle: string }

  // Admin check
  const ADMIN_EMAILS = ['jason@isd.ai', 'julian@isd.ai'];
  const isUserAdmin = user && ADMIN_EMAILS.includes(user.email);

  // Handle URL-based workflow ID navigation
  useEffect(() => {
    if (id) {
      setView('detail');
      setSelectedWorkflowId(id);
    } else {
      // If no ID in URL and we're in detail view, go back to browse
      if (view === 'detail') {
        setView('browse');
        setSelectedWorkflowId(null);
      }
    }
  }, [id]);

  // Check subscription status when user logs in
  useEffect(() => {
    if (isAuthenticated && user) {
      checkSubscriptionStatus();
    } else {
      setHasSubscription(false);
    }
  }, [isAuthenticated, user]);

  // Fetch workflows from API (debounced for search)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchWorkflows();
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [selectedCategory, sortBy, searchQuery, pagination.page, sourceFilter]);

  const checkSubscriptionStatus = async () => {
    setCheckingSubscription(true);
    try {
      const accessToken = await getValidAccessToken();

      const response = await fetch(`${API_BASE}/subscription_status`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setHasSubscription(data.hasActiveSubscription);
        setSubscriptionTier(data.subscription?.tier || 'free');
      } else {
        setHasSubscription(false);
        setSubscriptionTier('free');
      }
    } catch (err) {
      console.error('Error checking subscription:', err);
      setHasSubscription(false);
      setSubscriptionTier('free');
    } finally {
      setCheckingSubscription(false);
    }
  };

  const handleLike = async (listingId) => {
    if (!isAuthenticated) {
      alert('Please sign in to like workflows');
      return;
    }

    try {
      const accessToken = await getValidAccessToken();

      // Optimistic update
      const wasLiked = likedWorkflows.has(listingId);
      const newLikedWorkflows = new Set(likedWorkflows);

      if (wasLiked) {
        newLikedWorkflows.delete(listingId);
      } else {
        newLikedWorkflows.add(listingId);
      }
      setLikedWorkflows(newLikedWorkflows);

      // Update likes count optimistically
      const updateWorkflowLikes = (workflow) => {
        if (workflow.id === listingId) {
          return {
            ...workflow,
            likes_count: wasLiked ? (workflow.likes_count || 1) - 1 : (workflow.likes_count || 0) + 1
          };
        }
        return workflow;
      };

      setWorkflows(prevWorkflows => prevWorkflows.map(updateWorkflowLikes));
      setMyWorkflows(prevMyWorkflows => prevMyWorkflows.map(updateWorkflowLikes));

      // Make API call
      const response = await fetch(`${API_BASE}/marketplace-like`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ listingId })
      });

      if (!response.ok) {
        throw new Error('Failed to toggle like');
      }

      const data = await response.json();

      // Update with actual server data (only update if counts differ from optimistic update)
      setWorkflows(prevWorkflows =>
        prevWorkflows.map(workflow =>
          workflow.id === listingId
            ? { ...workflow, likes_count: data.likesCount }
            : workflow
        )
      );

      setMyWorkflows(prevMyWorkflows =>
        prevMyWorkflows.map(workflow =>
          workflow.id === listingId
            ? { ...workflow, likes_count: data.likesCount }
            : workflow
        )
      );

    } catch (err) {
      console.error('Error toggling like:', err);

      // Revert optimistic updates on error
      const revertLikedWorkflows = new Set(likedWorkflows);
      setLikedWorkflows(revertLikedWorkflows);

      // Revert like counts
      const revertWorkflowLikes = (workflow) => {
        if (workflow.id === listingId) {
          return {
            ...workflow,
            likes_count: likedWorkflows.has(listingId)
              ? (workflow.likes_count || 0) + 1
              : (workflow.likes_count || 1) - 1
          };
        }
        return workflow;
      };

      setWorkflows(prevWorkflows => prevWorkflows.map(revertWorkflowLikes));
      setMyWorkflows(prevMyWorkflows => prevMyWorkflows.map(revertWorkflowLikes));
    }
  };

  const fetchWorkflows = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: 24,
        sort: sortBy,
        type: 'workflow',
      });

      if (selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }

      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }

      if (sourceFilter === 'private') {
        params.append('visibility', 'private');
      } else if (sourceFilter === 'official') {
        params.append('is_official', 'true');
      } else if (sourceFilter === 'community') {
        params.append('is_official', 'false');
      }

      // Private browse requires auth header
      const fetchHeaders = {};
      if (sourceFilter === 'private') {
        const accessToken = await getValidAccessToken();
        fetchHeaders['Authorization'] = `Bearer ${accessToken}`;
      }

      const response = await fetch(`${API_BASE}/marketplace?${params}`, {
        headers: fetchHeaders
      });

      if (!response.ok) {
        throw new Error('Failed to fetch workflows');
      }

      const data = await response.json();
      setWorkflows(data.workflows || []);
      setPagination(data.pagination || { page: 1, totalPages: 0, total: 0 });
    } catch (err) {
      console.error('Error fetching workflows:', err);
      setError(err.message);
      setWorkflows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyWorkflows = async () => {
    if (!isAuthenticated) return;

    setMyWorkflowsLoading(true);
    setMyWorkflowsError(null);

    try {
      const accessToken = await getValidAccessToken();

      const response = await fetch(`${API_BASE}/marketplace-my-workflows`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch your workflows');
      }

      const data = await response.json();
      setMyWorkflows(data.workflows || []);
    } catch (err) {
      console.error('Error fetching my workflows:', err);
      setMyWorkflowsError(err.message);
      setMyWorkflows([]);
    } finally {
      setMyWorkflowsLoading(false);
    }
  };

  const fetchMyDownloads = async () => {
    if (!isAuthenticated) return;

    setMyDownloadsLoading(true);
    setMyDownloadsError(null);

    try {
      const accessToken = await getValidAccessToken();

      const response = await fetch(`${API_BASE}/marketplace-my-downloads`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch your downloads');
      }

      const data = await response.json();
      setMyDownloads(data.downloads || []);
    } catch (err) {
      console.error('Error fetching my downloads:', err);
      setMyDownloadsError(err.message);
      setMyDownloads([]);
    } finally {
      setMyDownloadsLoading(false);
    }
  };

  // Admin: Fetch review queue
  const fetchReviewQueue = async () => {
    if (!isAuthenticated || !isUserAdmin) return;

    setReviewQueueLoading(true);
    setReviewQueueError(null);

    try {
      const accessToken = await getValidAccessToken();

      const response = await fetch(`${API_BASE}/marketplace-admin?action=queue`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch review queue');
      }

      const data = await response.json();
      setReviewQueue(data.workflows || []);
    } catch (err) {
      console.error('Error fetching review queue:', err);
      setReviewQueueError(err.message);
      setReviewQueue([]);
    } finally {
      setReviewQueueLoading(false);
    }
  };

  // Admin: Approve workflow
  const handleApproveWorkflow = async (listingId) => {
    if (!isAuthenticated || !isUserAdmin) return;

    try {
      const accessToken = await getValidAccessToken();

      const response = await fetch(`${API_BASE}/marketplace-admin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'approve', listingId })
      });

      if (!response.ok) {
        throw new Error('Failed to approve workflow');
      }

      // Refresh the review queue
      fetchReviewQueue();
    } catch (err) {
      console.error('Error approving workflow:', err);
      alert('Failed to approve workflow: ' + err.message);
    }
  };

  // Admin: Reject workflow
  const handleRejectWorkflow = async (listingId, feedbackType = 'general', notes = '') => {
    if (!isAuthenticated || !isUserAdmin) return;

    try {
      const accessToken = await getValidAccessToken();

      const response = await fetch(`${API_BASE}/marketplace-admin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'reject', listingId, feedbackType, notes })
      });

      if (!response.ok) {
        throw new Error('Failed to reject workflow');
      }

      // Refresh the review queue
      fetchReviewQueue();
    } catch (err) {
      console.error('Error rejecting workflow:', err);
      throw err; // Re-throw for modal to handle
    }
  };

  // Admin: Open feedback panel to view conversation
  const openAdminFeedbackPanel = async (workflow) => {
    try {
      const token = await getValidAccessToken();
      setAdminFeedbackAccessToken(token);
      setViewingFeedbackWorkflow(workflow);
    } catch (err) {
      console.error('Error opening feedback panel:', err);
    }
  };

  // Admin: Fetch deleted workflows
  const fetchDeletedWorkflows = async () => {
    if (!isAuthenticated || !isUserAdmin) return;

    setDeletedWorkflowsLoading(true);
    setDeletedWorkflowsError(null);

    try {
      const accessToken = await getValidAccessToken();

      const response = await fetch(`${API_BASE}/marketplace-admin?action=deleted`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch deleted workflows');
      }

      const data = await response.json();
      setDeletedWorkflows(data.workflows || []);
    } catch (err) {
      console.error('Error fetching deleted workflows:', err);
      setDeletedWorkflowsError(err.message);
      setDeletedWorkflows([]);
    } finally {
      setDeletedWorkflowsLoading(false);
    }
  };

  // Admin: Hard delete workflow (permanently remove from S3 + database)
  const handleAdminHardDelete = async (workflow) => {
    if (!isAuthenticated || !isUserAdmin) return;

    const confirmed = window.confirm(
      `PERMANENT DELETE: "${workflow.title}"\n\n` +
      `This will:\n` +
      `- Remove all files from S3\n` +
      `- Delete the database record permanently\n\n` +
      `This action cannot be undone. Continue?`
    );

    if (!confirmed) return;

    setHardDeleting(workflow.id);

    try {
      const accessToken = await getValidAccessToken();

      const response = await fetch(`${API_BASE}/marketplace-delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ listingId: workflow.id, hardDelete: true })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to hard delete workflow');
      }

      // Refresh the deleted workflows list
      fetchDeletedWorkflows();
    } catch (err) {
      console.error('Error hard deleting workflow:', err);
      alert('Failed to hard delete workflow: ' + err.message);
    } finally {
      setHardDeleting(null);
    }
  };

  // ============================================
  // MAIN RENDER
  // ============================================
  if (view === 'browse') return (
    <BrowseView
      workflows={workflows}
      loading={loading}
      error={error}
      pagination={pagination}
      setPagination={setPagination}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      selectedCategory={selectedCategory}
      setSelectedCategory={setSelectedCategory}
      sortBy={sortBy}
      setSortBy={setSortBy}
      isAuthenticated={isAuthenticated}
      hasSubscription={hasSubscription}
      setView={setView}
      setSelectedWorkflowId={setSelectedWorkflowId}
      fetchWorkflows={fetchWorkflows}
      fetchMyWorkflows={fetchMyWorkflows}
      fetchMyDownloads={fetchMyDownloads}
      handleLike={handleLike}
      likedWorkflows={likedWorkflows}
      navigate={navigate}
      isEmbedded={isEmbedded}
      isUserAdmin={isUserAdmin}
      fetchReviewQueue={fetchReviewQueue}
      fetchDeletedWorkflows={fetchDeletedWorkflows}
      sourceFilter={sourceFilter}
      setSourceFilter={setSourceFilter}
      subscriptionTier={subscriptionTier}
      successBanner={successBanner}
      setSuccessBanner={setSuccessBanner}
      getValidAccessToken={getValidAccessToken}
    />
  );

  if (view === 'my-workflows') return (
    <MyWorkflowsView
      myWorkflows={myWorkflows}
      myWorkflowsLoading={myWorkflowsLoading}
      myWorkflowsError={myWorkflowsError}
      setView={setView}
      setSelectedWorkflowId={setSelectedWorkflowId}
      setEditingWorkflowId={setEditingWorkflowId}
      fetchMyWorkflows={fetchMyWorkflows}
      fetchMyDownloads={fetchMyDownloads}
      navigate={navigate}
      isEmbedded={isEmbedded}
      getValidAccessToken={getValidAccessToken}
      successBanner={successBanner}
      setSuccessBanner={setSuccessBanner}
      subscriptionTier={subscriptionTier}
    />
  );

  if (view === 'my-downloads') return (
    <MyDownloadsView
      myDownloads={myDownloads}
      myDownloadsLoading={myDownloadsLoading}
      myDownloadsError={myDownloadsError}
      setView={setView}
      setSelectedWorkflowId={setSelectedWorkflowId}
      fetchMyWorkflows={fetchMyWorkflows}
      fetchMyDownloads={fetchMyDownloads}
      navigate={navigate}
      isEmbedded={isEmbedded}
    />
  );

  // Admin Review Queue View
  if (view === 'review-queue' && isUserAdmin) return (
    <div className={`min-h-screen bg-black text-white ${isEmbedded ? 'pt-4 px-4 py-4' : 'pt-20 px-4 py-12'}`}>
      <div className="max-w-7xl mx-auto">
        {!isEmbedded && (
          <div className="mb-8">
            <h1 className="text-4xl font-bold">Admin Review Queue</h1>
            <p className="text-gray-400 mt-2">Review and approve workflow submissions</p>
          </div>
        )}

        <div className="mb-6 flex flex-wrap items-center gap-4">
          <button
            onClick={() => setView('browse')}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Browse
          </button>
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search by title, creator, or category..."
              value={reviewQueueSearch}
              onChange={(e) => setReviewQueueSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
            />
          </div>
        </div>

        {reviewQueueLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
              <p className="mt-4 text-gray-400">Loading review queue...</p>
            </div>
          </div>
        ) : reviewQueueError ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <p className="text-red-500 mb-2">Error loading review queue</p>
              <p className="text-gray-400 text-sm">{reviewQueueError}</p>
              <button
                onClick={fetchReviewQueue}
                className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : reviewQueue.length === 0 ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <p className="text-gray-400 text-lg mb-2">No workflows pending review</p>
              <p className="text-gray-500 text-sm">All caught up!</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {reviewQueue.filter((workflow) => {
              if (!reviewQueueSearch.trim()) return true;
              const q = reviewQueueSearch.toLowerCase();
              return (
                (workflow.title || '').toLowerCase().includes(q) ||
                (workflow.creator_name || '').toLowerCase().includes(q) ||
                (workflow.creator_email || '').toLowerCase().includes(q) ||
                (workflow.category || '').toLowerCase().includes(q) ||
                (workflow.description || '').toLowerCase().includes(q)
              );
            }).map((workflow) => (
              <div key={workflow.id} className="bg-gray-900 border border-gray-800 rounded-lg p-6 overflow-hidden">
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                  {/* Thumbnail */}
                  <div className="w-full sm:w-32 aspect-[3/4] bg-gray-800 rounded-lg overflow-hidden flex-shrink-0">
                    {workflow.thumbnail_url ? (
                      <img
                        src={workflow.thumbnail_url}
                        alt={workflow.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="h-8 w-8 text-gray-600" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white mb-2">{workflow.title}</h3>
                    <p className="text-gray-400 text-sm mb-2 line-clamp-2">{workflow.description}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500 mb-4">
                      <span>By: {workflow.creator_name || workflow.creator_email}</span>
                      <span>Category: {workflow.category}</span>
                      <span>Submitted: {new Date(workflow.created_at).toLocaleDateString()}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={() => handleApproveWorkflow(workflow.id)}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => setRejectingWorkflow(workflow)}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                      >
                        Reject
                      </button>
                      <button
                        onClick={() => {
                          navigate(`/exchange/${workflow.id}`);
                        }}
                        className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => openAdminFeedbackPanel(workflow)}
                        className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                      >
                        <MessageSquare className="h-4 w-4" />
                        View Feedback
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rejection Modal */}
      <RejectionModal
        workflow={rejectingWorkflow}
        isOpen={!!rejectingWorkflow}
        onClose={() => setRejectingWorkflow(null)}
        onReject={async (listingId, feedbackType, notes) => {
          await handleRejectWorkflow(listingId, feedbackType, notes);
          setRejectingWorkflow(null);
        }}
      />

      {/* Admin Feedback Panel */}
      <AdminFeedbackPanel
        listingId={viewingFeedbackWorkflow?.id}
        listingTitle={viewingFeedbackWorkflow?.title}
        isOpen={!!viewingFeedbackWorkflow}
        onClose={() => {
          setViewingFeedbackWorkflow(null);
          setAdminFeedbackAccessToken(null);
        }}
        accessToken={adminFeedbackAccessToken}
      />
    </div>
  );

  // Admin Deleted Workflows View
  if (view === 'deleted-workflows' && isUserAdmin) return (
    <div className={`min-h-screen bg-black text-white ${isEmbedded ? 'pt-4 px-4 py-4' : 'pt-20 px-4 py-12'}`}>
      <div className="max-w-7xl mx-auto">
        {!isEmbedded && (
          <div className="mb-8">
            <h1 className="text-4xl font-bold">Deleted Workflows</h1>
            <p className="text-gray-400 mt-2">Manage soft-deleted workflows from all users. Hard delete to remove files from S3.</p>
          </div>
        )}

        <div className="mb-6">
          <button
            onClick={() => setView('browse')}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Browse
          </button>
        </div>

        {deletedWorkflowsLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-red-600 border-r-transparent"></div>
              <p className="mt-4 text-gray-400">Loading deleted workflows...</p>
            </div>
          </div>
        ) : deletedWorkflowsError ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <p className="text-red-500 mb-2">Error loading deleted workflows</p>
              <p className="text-gray-400 text-sm">{deletedWorkflowsError}</p>
              <button
                onClick={fetchDeletedWorkflows}
                className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : deletedWorkflows.length === 0 ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <p className="text-gray-400 text-lg mb-2">No deleted workflows</p>
              <p className="text-gray-500 text-sm">All clean!</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {deletedWorkflows.map((workflow) => (
              <div key={workflow.id} className="bg-gray-900 border border-red-900/50 rounded-lg p-4">
                <div className="flex gap-4">
                  {/* Thumbnail */}
                  <div className="w-24 aspect-[3/4] bg-gray-800 rounded-lg overflow-hidden flex-shrink-0">
                    {workflow.thumbnail_url ? (
                      <img
                        src={workflow.thumbnail_url}
                        alt={workflow.title}
                        className="w-full h-full object-cover opacity-50"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="h-6 w-6 text-gray-600" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-white truncate">{workflow.title}</h3>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500 mt-1">
                      <span>Creator: {workflow.creator_name || workflow.creator_email || 'Unknown'}</span>
                      <span>Category: {workflow.category}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500 mt-1">
                      <span className="text-red-400">
                        Deleted: {new Date(workflow.deleted_at).toLocaleString()}
                      </span>
                      <span>
                        By: {workflow.deleted_by_name || workflow.deleted_by_email || 'Unknown'}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleAdminHardDelete(workflow)}
                      disabled={hardDeleting === workflow.id}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      {hardDeleting === workflow.id ? 'Deleting...' : 'Hard Delete'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (view === 'comments-log') return (
    <CommentsLog
      setView={setView}
      setSelectedWorkflowId={setSelectedWorkflowId}
      navigate={navigate}
      isEmbedded={isEmbedded}
    />
  );

  if (view === 'detail') return (
    <DetailView
      selectedWorkflowId={selectedWorkflowId}
      user={user}
      isAuthenticated={isAuthenticated}
      getValidAccessToken={getValidAccessToken}
      likedWorkflows={likedWorkflows}
      setLikedWorkflows={setLikedWorkflows}
      setView={setView}
      fetchWorkflows={fetchWorkflows}
      handleLike={handleLike}
      hasSubscription={hasSubscription}
      setEditingWorkflowId={setEditingWorkflowId}
      navigate={navigate}
      isEmbedded={isEmbedded}
      subscriptionTier={subscriptionTier}
    />
  );

  if (view === 'upload') return (
    <>
      <Helmet>
        <title>Upload Workflow - ContextUI Exchange</title>
        <meta name="description" content="Upload and share your ComfyUI workflows and AI tools with the community." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <UploadView
      editingWorkflowId={editingWorkflowId}
      setEditingWorkflowId={setEditingWorkflowId}
      getValidAccessToken={getValidAccessToken}
      setView={setView}
      myDownloads={myDownloads}
      fetchMyDownloads={fetchMyDownloads}
      fetchWorkflows={fetchWorkflows}
      fetchMyWorkflows={fetchMyWorkflows}
      setSuccessBanner={setSuccessBanner}
      subscriptionTier={subscriptionTier}
      setSourceFilter={setSourceFilter}
    />
    </>
  );

  return (
    <>
      <Helmet>
        <title>AI Tools Marketplace - ContextUI Exchange</title>
        <meta name="description" content="Browse and deploy AI workflows. Discover official and community ComfyUI workflows and GPU-powered AI tools." />
        <meta property="og:title" content="AI Tools Marketplace - ContextUI Exchange" />
        <meta property="og:description" content="Browse and deploy AI workflows. Discover official and community ComfyUI workflows and GPU-powered AI tools." />
        <meta property="og:url" content="https://contextui.ai/exchange" />
        <link rel="canonical" href="https://contextui.ai/exchange" />
      </Helmet>
      {null}
    </>
  );
};

export default Exchange;
